<template>
    <div>
        <HeaderContent :title="isEdit ? 'Edit Offer Document' : 'Create New Offer'" />

        <!-- Modals -->
        <TransportationForm v-if="modals.transportation" :modal="modals.transportation" :selectedData="selectedItem"
            :defaultPax="formOffer.pax" :action="selectedItem.index !== undefined ? 'edit' : 'add'"
            :totalDays="formOffer.programs.length" :currentDayIndex="activeDayIndex"
            @update:modal="(val) => modals.transportation = val" @addOrUpdate="handleSaveTransportation" />

        <AccommodationForm v-if="modals.accommodation" :modal="modals.accommodation" :selectedData="selectedItem"
            :defaultPax="formOffer.pax" @update:modal="(val) => modals.accommodation = val"
            @addOrUpdate="handleSaveItem('accommodations', $event)" />

        <ActivityForm v-if="modals.activity" :modal="modals.activity" :selectedData="selectedItem"
            :defaultPax="formOffer.pax" @update:modal="(val) => modals.activity = val"
            @addOrUpdate="handleSaveItem('activities', $event)" />

        <MealForm v-if="modals.meals" :modal="modals.meals" :selectedData="selectedItem" :defaultPax="formOffer.pax"
            @update:modal="(val) => modals.meals = val" @addOrUpdate="handleSaveItem('meals', $event)" />

        <BForm @submit.stop.prevent @input="userInteracted = true" @change="userInteracted = true">
            <BRow>
                <BCol cols="12">
                    <BAlert v-if="isApprovedOrPaid" :model-value="true" variant="warning" class="mb-3 text-left">
                        <i class="fas fa-exclamation-triangle me-2"></i>
                        <strong>Attention:</strong> This offer is already <strong>{{ formOffer.status }}</strong>.
                        Changing the currency or other financial details will recalculate the totals and update existing
                        payment reflections.
                    </BAlert>

                    <!-- Preset Section -->
                    <BCard v-if="!isEdit" class="mb-3 border-info shadow-sm">
                        <template #header>
                            <h5 class="mb-0 text-info font-weight-bold text-left">Load from Package</h5>
                        </template>
                        <BRow class="align-items-end">
                            <BCol lg="9" class="text-left mb-3 mb-lg-0">
                                <label class="form-label">Select Package</label>
                                <v-select v-model="selectedPackageUuid" :options="packages" :reduce="pkg => pkg.uuid"
                                    label="name" placeholder="-- Search Package --" style="background-color: white"
                                    :loading="loadingStates.packages" @open="loadPackages">
                                    <template #option="option">
                                        <div class="d-flex justify-content-between">
                                            <span>{{ option.name }}</span>
                                            <small class="text-muted">{{ option.duration_days }} Days | {{
                                                formatRupiah(option.price) }}</small>
                                        </div>
                                    </template>
                                </v-select>
                            </BCol>
                            <BCol lg="3">
                                <BButton class="mt-2 w-100" @click="loadPreset" :disabled="!selectedPackageUuid"
                                    variant="success" c>
                                    Load Package
                                </BButton>
                            </BCol>
                        </BRow>
                        <p class="small text-danger mt-2 mb-0 text-left"><i class="fas fa-exclamation-triangle"></i>
                            Warning: Loading a template
                            will overwrite your current itinerary.</p>
                    </BCard>

                    <!-- 1. BASIC INFO SECTION -->
                    <BCard class="mb-4 shadow-sm border-0">
                        <template #header>
                            <h5 class="mb-0 font-weight-bold text-primary text-left">1. Offer Document Information</h5>
                        </template>
                        <div class="p-2">
                            <BRow>
                                <BCol lg="6" class="text-left mb-3 mb-lg-0">
                                    <BFormGroup label="Guest *" label-for="guest">
                                        <v-select v-model="formOffer.guest_uuid" :options="guests"
                                            :reduce="guest => guest.uuid"
                                            :get-option-label="guest => `${guest.alias + '. ' || ''} ${guest.fullname}`"
                                            placeholder="Search Guest Name..." style="background-color: white"
                                            :class="{ 'is-invalid': v$.formOffer.guest_uuid.$dirty && v$.formOffer.guest_uuid.$invalid }"
                                            :loading="loadingStates.guests" @open="loadGuests" />
                                        <BFormInvalidFeedback
                                            :state="!(v$.formOffer.guest_uuid.$dirty && v$.formOffer.guest_uuid.$invalid)">
                                            {{ v$.formOffer.guest_uuid.$errors[0]?.$message }}
                                        </BFormInvalidFeedback>
                                    </BFormGroup>
                                </BCol>
                                <BCol lg="6" class="text-left mb-3 mb-lg-0">
                                    <BFormGroup label="Offer Title / Name *" label-for="name">
                                        <BFormInput v-model="formOffer.name" placeholder="e.g. Medan City Tour 2 Days"
                                            :class="{ 'is-invalid': v$.formOffer.name.$dirty && v$.formOffer.name.$invalid }" />
                                        <BFormInvalidFeedback
                                            :state="!(v$.formOffer.name.$dirty && v$.formOffer.name.$invalid)">
                                            {{ v$.formOffer.name.$errors[0]?.$message }}
                                        </BFormInvalidFeedback>
                                    </BFormGroup>
                                </BCol>
                            </BRow>

                            <BRow class="mt-3 text-left">
                                <BCol lg="3" class="mb-3 mb-lg-0">
                                    <BFormGroup label="Transaction Date" label-for="trans_date">
                                        <BFormInput type="date" v-model="formOffer.trans_date" />
                                    </BFormGroup>
                                </BCol>
                                <BCol lg="3" class="mb-3 mb-lg-0">
                                    <BFormGroup label="Start Date (Arrival)" label-for="start">
                                        <BFormInput type="date" v-model="formOffer.start" @change="calculateDuration"
                                            :class="{ 'is-invalid': v$.formOffer.start.$dirty && v$.formOffer.start.$invalid }" />
                                        <BFormInvalidFeedback
                                            :state="!(v$.formOffer.start.$dirty && v$.formOffer.start.$invalid)">
                                            {{ v$.formOffer.start.$errors[0]?.$message }}
                                        </BFormInvalidFeedback>
                                    </BFormGroup>
                                </BCol>
                                <BCol lg="3" class="mb-3 mb-lg-0">
                                    <BFormGroup label="End Date (Departure)" label-for="end">
                                        <BFormInput type="date" v-model="formOffer.end" @change="calculateDuration"
                                            :class="{ 'is-invalid': v$.formOffer.end.$dirty && v$.formOffer.end.$invalid }" />
                                        <BFormInvalidFeedback
                                            :state="!(v$.formOffer.end.$dirty && v$.formOffer.end.$invalid)">
                                            {{ v$.formOffer.end.$errors[0]?.$message }}
                                        </BFormInvalidFeedback>
                                    </BFormGroup>
                                </BCol>
                                <BCol lg="3" class="mb-3 mb-lg-0">
                                    <BFormGroup label="Duration (Days)" label-for="duration">
                                        <BFormInput type="number" v-model="formOffer.duration_days" />
                                    </BFormGroup>
                                </BCol>
                            </BRow>

                            <BRow class="mt-3 text-left">
                                <BCol lg="3" class="mb-3 mb-lg-0">
                                    <BFormGroup label="Pax *" label-for="pax">
                                        <BFormInput type="number" v-model="formOffer.pax" min="1"
                                            :class="{ 'is-invalid': v$.formOffer.pax.$dirty && v$.formOffer.pax.$invalid }" />
                                        <BFormInvalidFeedback
                                            :state="!(v$.formOffer.pax.$dirty && v$.formOffer.pax.$invalid)">
                                            {{ v$.formOffer.pax.$errors[0]?.$message }}
                                        </BFormInvalidFeedback>
                                    </BFormGroup>
                                </BCol>
                                <BCol lg="3" class="mb-3 mb-lg-0">
                                    <BFormGroup label="Currency *" label-for="currency">
                                        <v-select v-model="formOffer.currency_uuid" :options="currencies"
                                            :reduce="c => c.uuid"
                                            :get-option-label="option => `${option.name} (${option.symbol})`"
                                            placeholder="Select Currency" style="background-color: white"
                                            :class="{ 'is-invalid': v$.formOffer.currency_uuid.$dirty && v$.formOffer.currency_uuid.$invalid }"
                                            :loading="loadingStates.currencies" @open="loadCurrencies" />
                                        <BFormInvalidFeedback
                                            :state="!(v$.formOffer.currency_uuid.$dirty && v$.formOffer.currency_uuid.$invalid)">
                                            {{ v$.formOffer.currency_uuid.$errors[0]?.$message }}
                                        </BFormInvalidFeedback>
                                    </BFormGroup>
                                </BCol>
                                <BCol lg="3" class="mb-3 mb-lg-0">
                                    <BFormGroup label="Exchange Rate *"
                                        :description="selectedCurrency ? `${1} ${selectedCurrency?.code} = ${formOffer.exchange_rate} IDR` : ''"
                                        label-for="rate">
                                        <BFormInput type="number" step="any" v-model="formOffer.exchange_rate"
                                            :class="{ 'is-invalid': v$.formOffer.exchange_rate.$dirty && v$.formOffer.exchange_rate.$invalid }" />
                                        <BFormInvalidFeedback
                                            :state="!(v$.formOffer.exchange_rate.$dirty && v$.formOffer.exchange_rate.$invalid)">
                                            {{ v$.formOffer.exchange_rate.$errors[0]?.$message }}
                                        </BFormInvalidFeedback>
                                    </BFormGroup>
                                </BCol>
                                <BCol lg="3" class="mb-3 mb-lg-0">
                                    <BFormGroup label="Status *" label-for="status">
                                        <BFormSelect v-model="formOffer.status" :options="statusOptions" />
                                    </BFormGroup>
                                </BCol>
                            </BRow>

                            <BRow class="mt-3 text-left">
                                <BCol lg="3" class="mb-3 mb-lg-0">
                                    <BFormGroup label="Payment Method *" label-for="payment_method">
                                        <v-select v-model="formOffer.payment_method_uuid" :options="paymentMethods"
                                            :reduce="pm => pm.uuid"
                                            :get-option-label="pm => `${pm.name} - ${pm.number}`"
                                            placeholder="Select Payment Method" style="background-color: white"
                                            :class="{ 'is-invalid': v$.formOffer.payment_method_uuid.$dirty && v$.formOffer.payment_method_uuid.$invalid }"
                                            :loading="loadingStates.paymentMethods" @open="loadPaymentMethods" />
                                        <BFormInvalidFeedback
                                            :state="!(v$.formOffer.payment_method_uuid.$dirty && v$.formOffer.payment_method_uuid.$invalid)">
                                            {{ v$.formOffer.payment_method_uuid.$errors[0]?.$message }}
                                        </BFormInvalidFeedback>
                                    </BFormGroup>
                                </BCol>
                            </BRow>
                        </div>
                    </BCard>

                    <!-- 2. ITINERARY SECTION -->
                    <BCard class="mb-4 shadow-sm border-0">
                        <template #header>
                            <div class="d-flex justify-content-between align-items-center">
                                <h5 class="mb-0 font-weight-bold text-primary">2. Program Description</h5>
                            </div>
                        </template>
                        <div class="p-2">
                            <div v-for="(program, index) in formOffer.programs" :key="index"
                                class="card shadow-sm mb-4 border-left-primary">
                                <div class="card-header bg-light py-2">
                                    <div
                                        class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center">
                                        <h6 class="mb-2 mb-sm-0 font-weight-bold w-100 text-left">Day {{ program.day ||
                                            (index + 1) }} -
                                            <small class="text-muted">{{ getDayDate(index) }}</small>
                                        </h6>
                                        <div
                                            class="d-flex align-items-center w-sm-auto justify-content-between justify-content-sm-end">
                                            <BButton variant="link" class="text-danger p-0" @click="removeDay(index)">
                                                <i class="fas fa-trash"></i>
                                            </BButton>
                                        </div>
                                    </div>
                                </div>
                                <div class="card-body">
                                    <BRow class="text-left">
                                        <BCol lg="3" class="mb-3">
                                            <BFormGroup label="Date">
                                                <BFormInput type="date" v-model="program.date" />
                                            </BFormGroup>
                                        </BCol>
                                        <BCol lg="3" class="mb-3">
                                            <BFormGroup label="From Destination *">
                                                <v-select v-model="program.from_destination_uuid"
                                                    :options="destinations" :reduce="d => d.uuid" label="name"
                                                    placeholder="Pick Location"
                                                    :class="v$.formOffer.$dirty && v$.formOffer.programs.$each.$response.$errors[index]?.from_destination_uuid.length ? 'is-invalid' : ''"
                                                    @option:selected="program.name = ''"
                                                    :loading="loadingStates.destinations" @open="loadDestinations" />
                                                <BFormInvalidFeedback
                                                    :state="!(v$.formOffer.$dirty && v$.formOffer.programs.$each.$response.$errors[index]?.from_destination_uuid.length)">
                                                    {{
                                                        v$.formOffer.programs.$each.$response.$errors[index]?.from_destination_uuid[0]?.$message
                                                    }}
                                                </BFormInvalidFeedback>
                                            </BFormGroup>
                                        </BCol>
                                        <BCol lg="3" class="mb-3">
                                            <BFormGroup label="To Destination *">
                                                <v-select v-model="program.to_destination_uuid" :options="destinations"
                                                    :reduce="d => d.uuid" label="name" placeholder="Pick Location"
                                                    :class="v$.formOffer.$dirty && v$.formOffer.programs.$each.$response.$errors[index]?.to_destination_uuid.length ? 'is-invalid' : ''"
                                                    @option:selected="program.name = ''"
                                                    :loading="loadingStates.destinations" @open="loadDestinations" />
                                                <BFormInvalidFeedback
                                                    :state="!(v$.formOffer.$dirty && v$.formOffer.programs.$each.$response.$errors[index]?.to_destination_uuid.length)">
                                                    {{
                                                        v$.formOffer.programs.$each.$response.$errors[index]?.to_destination_uuid[0]?.$message
                                                    }}
                                                </BFormInvalidFeedback>
                                            </BFormGroup>
                                        </BCol>
                                        <BCol lg="3" class="mb-3">
                                            <BFormGroup label="Program Name (Pick or Create)">
                                                <v-select v-model="program.name" :options="getFilteredPrograms(program)"
                                                    label="name" :reduce="p => p && typeof p === 'object' ? p.name : p"
                                                    taggable
                                                    :disabled="!program.from_destination_uuid || !program.to_destination_uuid"
                                                    :class="v$.formOffer.$dirty && v$.formOffer.programs.$each.$response.$errors[index]?.name.length ? 'is-invalid' : ''"
                                                    @option:selected="(val) => selectProgramForDay(index, val)"
                                                    placeholder="e.g. Airport Transfer & Hotel Check-in"
                                                    style="background-color: white"
                                                    :loading="loadingStates.masterPrograms" @open="loadMasterPrograms">
                                                    <template #option="option">
                                                        <div class="d-flex flex-column">
                                                            <div
                                                                class="d-flex justify-content-between align-items-center text-left">
                                                                <span>{{ option.name }}</span>
                                                                <small class="badge bg-success px-2" v-if="option.uuid">
                                                                    <i class="fas fa-plus me-1"></i> Add Program
                                                                </small>
                                                                <small class="badge bg-success px-2" v-else>
                                                                    <i class="fas fa-plus me-1"></i> Create Program
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </template>

                                                    <template #no-options>
                                                        <!-- <div v-if="searching" class="p-3 text-center">
                                                <div class="text-muted small mb-2">No program named "{{ search }}"</div>
                                                <div class="border rounded p-2 bg-light d-flex justify-content-between align-items-center">
                                                    <span class="text-dark font-weight-bold truncate-text">{{ search }}</span>
                                                    <span class="badge bg-success ms-2">
                                                        <i class="fas fa-magic me-1"></i> Create Program
                                                    </span>
                                                </div>
                                                <div class="mt-2 x-small text-muted italic">Press Enter to confirm</div>
                                            </div> -->
                                                        <div class="p-3 text-muted small text-center">
                                                            <i class="fas fa-search me-1"></i> Type to search or create
                                                            program...
                                                        </div>
                                                    </template>
                                                </v-select>
                                                <BFormInvalidFeedback
                                                    :state="!(v$.formOffer.$dirty && v$.formOffer.programs.$each.$response.$errors[index]?.name.length)">
                                                    {{
                                                        v$.formOffer.programs.$each.$response.$errors[index]?.name[0]?.$message
                                                    }}
                                                </BFormInvalidFeedback>
                                            </BFormGroup>
                                        </BCol>
                                    </BRow>

                                    <!-- Sub-tabs for Day Items -->
                                    <div class="mt-3">
                                        <BTabs content-class="mt-3" active-nav-item-class="font-weight-bold">
                                            <!-- Transportation Tab -->
                                            <BTab title="Transportation" active>
                                                <template #title>
                                                    <i class="fas fa-car me-1"></i> Transportation
                                                </template>
                                                <div class="d-flex justify-content-end">
                                                    <BButton variant="outline-primary" size="xs" class="mb-2"
                                                        @click="openModalAdd('transportation', index)">
                                                        <i class="fas fa-plus"></i> Add Transportation
                                                    </BButton>
                                                </div>
                                                <table class="table table-sm table-bordered table-hover mb-0">
                                                    <thead class="bg-light x-small">
                                                        <tr>
                                                            <th>Vehicle</th>
                                                            <th>Pax</th>
                                                            <th>Price</th>
                                                            <th class="text-right">Subtotal</th>
                                                            <th width="80">Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody class="small">
                                                        <tr v-for="(item, idx) in program.transportations" :key="idx">
                                                            <td class="text-left font-weight-bold">{{ item.name }}</td>
                                                            <td>{{ item.pax }}</td>
                                                            <td class="text-right">
                                                                {{ formatRupiah(item.price) }}
                                                                 <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                                                    class="small text-muted italic">
                                                                    {{ formatCurrency(Math.round(Number(item.price || 0) /
                                                                        Number(formOffer.exchange_rate ||
                                                                            1))) }}
                                                                </div>
                                                            </td>
                                                            <td class="text-right">
                                                                {{ formatRupiah(item.sub_total || (item.price *
                                                                    item.pax)) }}
                                                                <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                                                    class="small text-muted italic">
                                                                    {{ formatCurrency(distributedForeign?.programs[index]?.transportations[idx] || 0) }}
                                                                </div>
                                                            </td>
                                                            <td class="text-center">
                                                                <BButton variant="link" class="text-warning p-0 me-2"
                                                                    @click="openModalEdit({ type: 'transportation', dayIndex: index, item: item, itemIndex: idx })">
                                                                    <i class="fas fa-edit"></i>
                                                                </BButton>
                                                                <BButton variant="link" class="text-danger p-0"
                                                                    @click="removeDayItem('transportations', index, idx)">
                                                                    <i class="fas fa-trash"></i>
                                                                </BButton>
                                                            </td>
                                                        </tr>
                                                        <tr v-if="!program.transportations?.length">
                                                            <td colspan="5" class="text-center text-muted italic p-3">No
                                                                transportation data
                                                                added</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </BTab>

                                            <!-- Accommodation Tab -->
                                            <BTab title="Accommodation">
                                                <template #title>
                                                    <i class="fas fa-hotel me-1"></i> Accommodation
                                                </template>
                                                <div class="d-flex justify-content-end">
                                                    <BButton variant="outline-primary" size="xs" class="mb-2"
                                                        @click="openModalAdd('accommodation', index)">
                                                        <i class="fas fa-plus"></i> Add Accommodation
                                                    </BButton>
                                                </div>
                                                <div class="table-responsive">
                                                    <table class="table table-sm table-bordered table-hover mb-0">
                                                        <thead class="bg-light x-small">
                                                            <tr>
                                                                <th>Hotel</th>
                                                                <th>Room Type</th>
                                                                <th class="text-center">Pax</th>
                                                                <th class="text-right">Price</th>
                                                                <th class="text-right">Subtotal</th>
                                                                <th class="text-center">Extra Bed</th>
                                                                <th class="text-right">Extra Bed Price</th>
                                                                <th class="text-right">Total Price</th>
                                                                <th width="80">Action</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody class="small">
                                                            <tr v-for="(item, idx) in program.accommodations"
                                                                :key="idx">
                                                                <td class="text-left font-weight-bold">
                                                                    {{ item.name }}
                                                                    <div v-if="item.is_single_supplement"
                                                                        class="small text-muted italic">
                                                                        Single Supplement
                                                                    </div>
                                                                </td>
                                                                <td>{{ item.room_type }}</td>
                                                                <td class="text-center">{{ item.pax }}</td>
                                                                <td class="text-right">
                                                                    {{ formatRupiah(item.price) }}
                                                                    <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                                                        class="small text-muted italic">
                                                                        {{ formatCurrency(Math.round(Number(item.price || 0) /
                                                                            Number(formOffer.exchange_rate ||
                                                                                1))) }}
                                                                    </div>
                                                                </td>
                                                                <td class="text-right">
                                                                    {{ formatRupiah(item.sub_total || (item.price *
                                                                        item.pax)) }}
                                                                    <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                                                        class="small text-muted italic">
                                                                        {{ formatCurrency(distributedForeign?.programs[index]?.accommodations[idx] || 0) }}
                                                                    </div>
                                                                </td>
                                                                <td class="text-center">
                                                                    <span class="badge"
                                                                        :class="item.extra_bed == 1 ? 'bg-success' : 'bg-danger'">
                                                                        {{ item.extra_bed == 1 ? 'Yes' : 'No' }}
                                                                    </span>
                                                                </td>
                                                                <td class="text-right">
                                                                    {{ formatRupiah(item.extra_bed_price) }}
                                                                    <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                                                        class="small text-muted italic">
                                                                        {{ formatCurrency(Number(item.extra_bed_price ||
                                                                            0)
                                                                            /
                                                                        Number(formOffer.exchange_rate || 0)) }}
                                                                    </div>
                                                                </td>
                                                                <td class="text-right fw-bold">
                                                                    {{ formatRupiah(item.total_price || ((item.price *
                                                                        item.pax) +
                                                                        (item.extra_bed_price || 0))) }}
                                                                    <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                                                        class="small text-muted italic">
                                                                        {{ formatCurrency(distributedForeign?.programs[index]?.accommodations[idx] || 0) }}
                                                                    </div>
                                                                </td>
                                                                <td class="text-center">
                                                                    <BButton variant="link"
                                                                        class="text-warning p-0 me-2"
                                                                        @click="openModalEdit({ type: 'accommodation', dayIndex: index, item: item, itemIndex: idx })">
                                                                        <i class="fas fa-edit"></i>
                                                                    </BButton>
                                                                    <BButton variant="link" class="text-danger p-0"
                                                                        @click="removeDayItem('accommodations', index, idx)">
                                                                        <i class="fas fa-trash"></i>
                                                                    </BButton>
                                                                </td>
                                                            </tr>
                                                            <tr v-if="!program.accommodations?.length">
                                                                <td colspan="9"
                                                                    class="text-center text-muted italic p-3">No
                                                                    hotel data added
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </BTab>

                                            <!-- Activities Tab -->
                                            <BTab title="Activities">
                                                <template #title>
                                                    <i class="fas fa-walking me-1"></i> Activities
                                                </template>
                                                <div class="d-flex justify-content-end">
                                                    <BButton variant="outline-primary" size="xs" class="mb-2"
                                                        @click="openModalAdd('activity', index)">
                                                        <i class="fas fa-plus"></i> Add Activity
                                                    </BButton>
                                                </div>
                                                <div class="table-responsive">
                                                    <table class="table table-sm table-bordered table-hover mb-0">
                                                        <thead class="bg-light x-small">
                                                            <tr>
                                                                <th>Activity</th>
                                                                <th>Pax</th>
                                                                <th>Price</th>
                                                                <th class="text-right">Subtotal</th>
                                                                <th width="80">Action</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody class="small">
                                                            <tr v-for="(item, idx) in program.activities" :key="idx">
                                                                <td class="text-left font-weight-bold">{{ item.name }}
                                                                </td>
                                                                <td>{{ item.pax }}</td>
                                                                <td class="text-right">
                                                                    {{ formatRupiah(item.price) }}
                                                                    <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                                                        class="small text-muted italic">
                                                                        {{ formatCurrency(Math.round(Number(item.price || 0) /
                                                                            Number(formOffer.exchange_rate ||
                                                                                1))) }}
                                                                    </div>
                                                                </td>
                                                                <td class="text-right">
                                                                    {{ formatRupiah(item.sub_total || (item.price *
                                                                        item.pax)) }}
                                                                    <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                                                        class="small text-muted italic">
                                                                        {{ formatCurrency(distributedForeign?.programs[index]?.activities[idx] || 0) }}
                                                                    </div>
                                                                </td>
                                                                <td class="text-center">
                                                                    <BButton variant="link"
                                                                        class="text-warning p-0 me-2"
                                                                        @click="openModalEdit({ type: 'activity', dayIndex: index, item: item, itemIndex: idx })">
                                                                        <i class="fas fa-edit"></i>
                                                                    </BButton>
                                                                    <BButton variant="link" class="text-danger p-0"
                                                                        @click="removeDayItem('activities', index, idx)">
                                                                        <i class="fas fa-trash"></i></BButton>
                                                                </td>
                                                            </tr>
                                                            <tr v-if="!program.activities?.length">
                                                                <td colspan="5"
                                                                    class="text-center text-muted italic p-3">No
                                                                    activity data added
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </BTab>

                                            <!-- Meals Tab -->
                                            <BTab title="Meals">
                                                <template #title>
                                                    <i class="fas fa-utensils me-1"></i> Meals
                                                </template>
                                                <div class="d-flex justify-content-end">
                                                    <BButton variant="outline-primary" size="xs" class="mb-2"
                                                        @click="openModalAdd('meals', index)">
                                                        <i class="fas fa-plus"></i> Add Meal
                                                    </BButton>
                                                </div>
                                                <div class="table-responsive">
                                                    <table class="table table-sm table-bordered table-hover mb-0">
                                                        <thead class="bg-light x-small">
                                                            <tr>
                                                                <th>Meal Name</th>
                                                                <th>Pax</th>
                                                                <th>Price</th>
                                                                <th class="text-right">Subtotal</th>
                                                                <th width="80">Action</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody class="small">
                                                            <tr v-for="(item, idx) in program.meals" :key="idx">
                                                                <td class="text-left font-weight-bold">{{ item.name }}
                                                                </td>
                                                                <td>{{ item.pax || formOffer.pax }}</td>
                                                                <td class="text-right">
                                                                    {{ formatRupiah(item.price) }}
                                                                    <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                                                        class="small text-muted italic">
                                                                        {{ formatCurrency(Math.round(Number(item.price || 0) /
                                                                            Number(formOffer.exchange_rate ||
                                                                                1))) }}
                                                                    </div>
                                                                </td>
                                                                <td class="text-right">
                                                                    {{ formatRupiah(item.sub_total || (item.price *
                                                                        (item.pax || formOffer.pax))) }}
                                                                    <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                                                        class="small text-muted italic">
                                                                        {{ formatCurrency(distributedForeign?.programs[index]?.meals[idx] || 0) }}
                                                                    </div>
                                                                </td>
                                                                <td class="text-center">
                                                                    <BButton variant="link"
                                                                        class="text-warning p-0 me-2"
                                                                        @click="openModalEdit({ type: 'meals', dayIndex: index, item: item, itemIndex: idx })">
                                                                        <i class="fas fa-edit"></i>
                                                                    </BButton>
                                                                    <BButton variant="link" class="text-danger p-0"
                                                                        @click="removeDayItem('meals', index, idx)"><i
                                                                            class="fas fa-trash"></i>
                                                                    </BButton>
                                                                </td>
                                                            </tr>
                                                            <tr v-if="!program.meals?.length">
                                                                <td colspan="5"
                                                                    class="text-center text-muted italic p-3">No
                                                                    meal data added
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </BTab>
                                        </BTabs>
                                    </div>
                                </div>
                            </div>

                            <div class="d-flex justify-content-center">
                                <BButton variant="success" @click="addDay">
                                    <i class="fas fa-plus-circle me-1"></i> Add Day
                                </BButton>
                            </div>
                        </div>
                    </BCard>

                    <!-- 3. PRICING & TERMS SECTION -->
                    <BCard class="mb-4 shadow-sm border-0">
                        <template #header>
                            <h5 class="mb-0 font-weight-bold text-primary text-left">3. Pricing</h5>
                        </template>
                        <div class="p-2">
                            <BRow>
                                <BCol xl="6" class="text-left mb-4">
                                    <BFormGroup label="Inclusions (What's Included)">
                                        <BFormTextarea v-model="formOffer.inclusion"
                                            placeholder="List items included in package..." rows="4" />
                                    </BFormGroup>
                                    <BFormGroup label="Exclusions (What's NOT Included)" class="mt-3">
                                        <BFormTextarea v-model="formOffer.exclusion"
                                            placeholder="List items NOT included..." rows="4" />
                                    </BFormGroup>
                                    <BFormGroup label="Comments or Special Instructions" class="mt-3">
                                        <BFormTextarea v-model="formOffer.comments" placeholder="Add comments here..."
                                            rows="4" />
                                    </BFormGroup>
                                    <BFormGroup label="Passenger/Luggage Capacity" class="mt-3">
                                        <BFormTextarea v-model="formOffer.passanger_info"
                                            placeholder="Add passenger/luggage capacity here..." rows="4" />
                                    </BFormGroup>
                                    <BFormGroup label="Terms & Conditions" class="mt-3">
                                        <BFormTextarea v-model="formOffer.term_condition"
                                            placeholder="Add terms and conditions here..." rows="8" />
                                    </BFormGroup>
                                </BCol>
                                <BCol xl="6">
                                    <div class="card bg-light border-0 p-4 shadow-none mb-3">
                                        <div
                                            class="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                                            <h6 class="font-weight-bold mb-0">Additional Charges</h6>
                                            <BButton size="sm" variant="outline-primary" @click="openAdditionalModal()">
                                                <i class="fas fa-plus"></i> Add
                                            </BButton>
                                        </div>
                                        <div v-if="!formOffer.additionals || formOffer.additionals.length === 0"
                                            class="text-muted small text-center italic">
                                            No additional charges
                                        </div>
                                        <div v-else>
                                            <div v-for="(add, idx) in formOffer.additionals" :key="idx"
                                                class="d-flex justify-content-between align-items-center mb-2 small">
                                                <div>
                                                    <div class="text-left font-weight-bold">{{ add.name }}</div>
                                                    <div class="text-muted" v-if="add.pax">{{ formatRupiah(add.price) }}
                                                        x {{ add.pax }} Pax
                                                    </div>
                                                </div>
                                                <div class="text-end">
                                                    <div class="font-weight-bold">
                                                        {{ formatRupiah(add.sub_total || (add.price * add.pax)) }}
                                                        <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                                            class="small text-muted italic">
                                                            {{ formatCurrency(distributedForeign?.additionals[idx] || 0) }}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <button type="button" class="btn btn-link p-0"
                                                            @click="openAdditionalModal(add, idx)"><i
                                                                class="fas fa-edit text-info cursor-pointer me-2"></i></button>
                                                        <button type="button" class="btn btn-link p-0"
                                                            @click="removeAdditional(idx)"><i
                                                                class="fas fa-trash text-danger cursor-pointer"></i></button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div
                                                class="border-top pt-2 mt-2 d-flex justify-content-between font-weight-bold small">
                                                <span>Total Additional</span>
                                                <span>{{ formatRupiah(calculatedAdditionalTotal) }}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="card bg-light border-0 p-4 shadow-none mb-3">
                                        <h6 class="font-weight-bold mb-4 border-bottom pb-2 text-left">Tour Fees (Per
                                            Pax)</h6>
                                        <div class="table-responsive">
                                            <table class="table table-sm table-borderless mb-0">
                                                <tr>
                                                    <td class="text-left py-2">Entrance Fee</td>
                                                    <td>
                                                        <BFormInput type="number" step="1000"
                                                            v-model="formOffer.entrance_fee" class="text-right"
                                                            v-clear-zero />
                                                        <div v-if="formOffer.entrance_fee > 0"
                                                            class="small text-muted text-right mt-1 italic">
                                                            Total price ({{ formOffer.pax }} Pax): {{
                                                            formatRupiah((formOffer.pax || 1) *
                                                            formOffer.entrance_fee) }}
                                                            <span
                                                                v-if="selectedCurrency && selectedCurrency.code !== 'IDR'">
                                                                ({{ formatCurrency(distributedForeign?.tourFees.entrance || 0) }})
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td class="text-left py-2">Ferry Fee</td>
                                                    <td>
                                                        <BFormInput type="number" step="1000"
                                                            v-model="formOffer.ferry_fee" class="text-right"
                                                            v-clear-zero />
                                                        <div v-if="formOffer.ferry_fee > 0"
                                                            class="small text-muted text-right mt-1 italic">
                                                            Total price ({{ formOffer.pax }} Pax): {{
                                                            formatRupiah((formOffer.pax || 1) *
                                                            formOffer.ferry_fee) }}
                                                            <span
                                                                v-if="selectedCurrency && selectedCurrency.code !== 'IDR'">
                                                                ({{ formatCurrency(distributedForeign?.tourFees.ferry || 0) }})
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td class="text-left py-2">Toll Fee</td>
                                                    <td>
                                                        <BFormInput type="number" step="1000"
                                                            v-model="formOffer.toll_fee" class="text-right"
                                                            v-clear-zero />
                                                        <div v-if="formOffer.toll_fee > 0"
                                                            class="small text-muted text-right mt-1 italic">
                                                            Total price ({{ formOffer.pax }} Pax): {{
                                                            formatRupiah((formOffer.pax || 1) *
                                                            formOffer.toll_fee) }}
                                                            <span
                                                                v-if="selectedCurrency && selectedCurrency.code !== 'IDR'">
                                                                ({{ formatCurrency(distributedForeign?.tourFees.toll || 0) }})
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td class="text-left py-2">NP Permit Fee</td>
                                                    <td>
                                                        <BFormInput type="number" step="1000"
                                                            v-model="formOffer.np_permit_fee" class="text-right"
                                                            v-clear-zero />
                                                        <div v-if="formOffer.np_permit_fee > 0"
                                                            class="small text-muted text-right mt-1 italic">
                                                            Total price ({{ formOffer.pax }} Pax): {{
                                                            formatRupiah((formOffer.pax || 1) *
                                                            formOffer.np_permit_fee) }}
                                                            <span
                                                                v-if="selectedCurrency && selectedCurrency.code !== 'IDR'">
                                                                ({{ formatCurrency(distributedForeign?.tourFees.npPermit || 0) }})
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            </table>
                                        </div>
                                    </div>
                                    <!-- Total Price -->
                                    <div class="card bg-light border-0 p-4 shadow-none">
                                        <h6 class="font-weight-bold mb-4 border-bottom pb-2 text-left">Total Price</h6>
                                        <div class="table-responsive">
                                            <table class="table table-sm table-borderless mb-0">
                                                <tr>
                                                    <td class="text-left">
                                                        Tour Package
                                                        <div v-if="formOffer.pax > 0" class="small text-info italic">
                                                            Price per person ({{ formOffer.pax }} Pax): {{
                                                                formatRupiah(programSubtotal /
                                                                    formOffer.pax) }}<br>
                                                            ({{ formatCurrency(Math.round((distributedForeign?.programs.reduce((acc, p) => acc + p.total, 0) || 0) /
                                                                (formOffer.pax || 1))) }})
                                                        </div>
                                                    </td>
                                                    <td class="text-right font-weight-bold">
                                                        {{ formatRupiah(programSubtotal) }}
                                                        <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                                            class="small text-muted italic">
                                                            ({{ formatCurrency(distributedForeign?.programs.reduce((acc, p) => acc + p.total, 0) || 0) }})
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td class="text-left py-2 border-top border-bottom border-light">
                                                        Total Additional
                                                        <div v-if="calculatedAdditionalTotal > 0"
                                                            class="small text-info italic">
                                                            Price per person ({{ formOffer.pax }} Pax): {{
                                                                formatRupiah(calculatedAdditionalTotal /
                                                                    formOffer.pax) }}<br>
                                                            ({{ formatCurrency(Math.round((distributedForeign?.additionals.reduce((acc, a) => acc + a, 0) || 0) /
                                                                (formOffer.pax || 1))) }})
                                                        </div>
                                                    </td>
                                                    <td class="text-right font-weight-bold">
                                                        {{ formatRupiah(calculatedAdditionalTotal) }}
                                                        <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                                            class="small text-muted italic">
                                                            ({{ formatCurrency(distributedForeign?.additionals.reduce((acc, a) => acc + a, 0) || 0) }})
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr v-if="formOffer.single_supplement > 0">
                                                    <td class="text-left">Single Supplement</td>
                                                    <td class="text-right font-weight-bold">
                                                        {{ formatRupiah(formOffer.single_supplement) }}
                                                        <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                                            class="small text-muted italic">
                                                            ({{ formatCurrency(distributedForeign?.singleSupplement || 0) }})
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr class="border-top">
                                                    <td class="text-left text-muted font-weight-bold">Total</td>
                                                    <td class="text-right font-weight-bold">
                                                        {{ formatRupiah(calculatedSubtotal) }}
                                                        <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                                            class="small text-muted italic">
                                                            ({{ formatCurrency(distributedForeign?.summary.subtotal || 0) }})
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr v-if="selectedCurrency && selectedCurrency.code !== 'IDR'">
                                                    <td class="text-left text-muted italic small">Exchange Rate applied
                                                    </td>
                                                    <td class="text-right small text-info font-weight-bold">
                                                        / {{ formatDecimal(formOffer.exchange_rate) }}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td class="text-left py-2 border-top border-bottom border-light">
                                                        Discount
                                                        <select v-model="discountType" @change="handleTypeChange"
                                                            class="form-select form-select-sm d-inline-block w-auto ms-2 ps-2 pe-4 py-0 border-0 bg-transparent text-primary fw-bold">
                                                            <option value="percent">%</option>
                                                            <option value="nominal">Nominal</option>
                                                        </select>
                                                    </td>
                                                    <td class="border-top border-bottom border-light">
                                                        <BFormInput v-if="discountType === 'percent'" type="number"
                                                            step="0.5" v-model="formOffer.discount" class="text-right"
                                                            v-clear-zero />
                                                        <BFormInput v-else type="number" step="1"
                                                            v-model="formOffer.discount_amount" class="text-right"
                                                            v-clear-zero />
                                                    </td>
                                                </tr>
                                                <tr v-if="formOffer.discount > 0 && discountType === 'percent'">
                                                    <td class="text-left text-danger small">Discount Amount</td>
                                                    <td class="text-right text-danger small">
                                                        - {{ formatRupiah(calculatedDiscountAmount) }}
                                                        <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                                            class="small">
                                                            - {{ formatCurrency(distributedForeign?.summary.discount || 0) }}
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td class="text-left py-2 border-bottom border-light align-middle">
                                                        Service (%)
                                                    </td>
                                                    <td class="border-bottom border-light py-2">
                                                        <BFormInput type="number" step="0.5" v-model="formOffer.service"
                                                            class="text-right" v-clear-zero />
                                                        <div v-if="formOffer.service > 0"
                                                            class="small text-right mt-1 text-muted italic">
                                                            + {{ formatRupiah(calculatedServiceAmount) }}
                                                            <span
                                                                v-if="selectedCurrency && selectedCurrency.code !== 'IDR'">
                                                                (+ {{ formatCurrency(distributedForeign?.summary.service || 0) }})
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td
                                                        class="text-left pt-2 pb-4 border-bottom border-light align-middle">
                                                        Tax (%)
                                                    </td>
                                                    <td class="border-bottom border-light pb-4 pt-2">
                                                        <BFormInput type="number" step="0.5" v-model="formOffer.tax"
                                                            class="text-right" v-clear-zero />
                                                        <div v-if="formOffer.tax > 0"
                                                            class="small text-right mt-1 text-muted italic">
                                                            + {{ formatRupiah(calculatedTaxAmount) }}
                                                            <span
                                                                v-if="selectedCurrency && selectedCurrency.code !== 'IDR'">
                                                                (+ {{ formatCurrency(distributedForeign?.summary.tax || 0) }})
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr class="border-top">
                                                    <td class="text-left pt-2 pb-2">Deposit (%)</td>
                                                    <td>
                                                        <BFormInput type="number" step="1" min="0" max="100"
                                                            v-model="formOffer.deposit_percentage" class="text-right"
                                                            v-clear-zero />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td class="text-left text-info small">Deposit Amount (IDR)</td>
                                                    <td class="text-right text-info small">
                                                        {{ formatRupiah(formOffer.deposit_amount) }}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td class="text-left py-2 italic small">Balance Due Date</td>
                                                    <td>
                                                        <BFormInput type="date" v-model="formOffer.balance_due_date"
                                                            class="text-right small" />
                                                    </td>
                                                </tr>
                                                <tr class="h6 border-top">
                                                    <td class="text-left pt-4 font-weight-bold text-primary">Grand Total (IDR)</td>
                                                    <td
                                                        class="text-right pt-4 text-primary font-weight-bold d-flex flex-column align-items-end">
                                                        {{ formatRupiah(finalTotal) }}
                                                        <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                                            class="h6 mb-0 text-muted d-flex align-items-center justify-content-end mt-2">
                                                            <span class="small italic">({{ formatCurrency(foreignCalculatedTotal) }})</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr class="h6 border-top bg-light">
                                                    <td class="text-left pt-3 pb-3 font-weight-bold text-success">Grand Total Sales (IDR)</td>
                                                    <td class="text-right pt-3 pb-3">
                                                        <BFormInput type="number" step="1" v-model.lazy="formOffer.grand_total_final" 
                                                            @update:model-value="onManualIdrChange"
                                                            class="text-right fw-bold text-success border-success" 
                                                            v-clear-zero />
                                                        <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                                            class="h6 mb-0 text-success d-flex align-items-center justify-content-end mt-2">
                                                            <span class="me-2">({{ selectedCurrency?.symbol || '' }})</span>
                                                            <BFormInput type="number" step="1" style="width: 150px"
                                                                v-model.lazy="foreignFinalTotalInput"
                                                                class="text-right fw-bold border-success text-success" />
                                                        </div>
                                                    </td>
                                                </tr>
                                                <!-- <tr class="h4">
                                    <td class="text-left pt-2 font-weight-bold text-success">Grand Total (IDR)</td>
                                    <td class="text-right pt-2 text-success font-weight-bold">
                                        {{ formatRupiah(finalTotal) }}
                                        <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'" class="h5 mb-0 text-muted">
                                            ({{ formatCurrency(foreignFinalTotal) }})
                                        </div>
                                    </td>
                                </tr> -->
                                            </table>
                                        </div>
                                    </div>
                                </BCol>
                            </BRow>
                        </div>
                        <template #footer>
                            <div class="d-flex justify-content-end">
                                <BButton @click="submitForm" variant="primary" class="me-2 px-4">
                                    <i class="fas fa-save me-1"></i> {{ isEdit ? 'Update' : 'Save' }} Offer
                                </BButton>
                                <BButton type="button" @click="() => router.back()" variant="danger" class="px-4">
                                    <i class="fas fa-times me-1"></i> Cancel
                                </BButton>
                            </div>
                        </template>
                    </BCard>



                </BCol>
            </BRow>
        </BForm>

        <BModal v-model="showPreview" size="xl" title="Offer Document Preview" scrollable>
            <template #header>
                <div class="w-100 d-flex justify-content-between align-items-center">
                    <h5 class="modal-title">Offer Document Preview</h5>
                    <div>
                        <!-- <BButton variant="success" size="sm" @click="printDocument" class="d-print-none me-2">
                    <i class="fas fa-print me-2"></i> Print Document
                </BButton> -->
                    </div>
                </div>
            </template>

            <template #footer>
                <div class="w-100 d-flex justify-content-end">
                    <BButton variant="secondary" @click="showPreview = false" class="me-2">
                        Close
                    </BButton>
                    <BButton v-if="isSavingFlow" variant="primary" @click="performSave">
                        <i class="fas fa-check-circle me-1"></i> {{ isEdit ? 'Confirm & Update' : 'Confirm & Save' }}
                    </BButton>
                </div>
            </template>

            <!-- MODAL PREVIEW: Data Summary Table (visible in modal) -->
            <div class="modal-preview-content p-3" style="font-family: 'Segoe UI', Arial, sans-serif;">

                <!-- Header Info (Minimalist Format) -->
                <div class="row mb-4 border-bottom pb-4 px-2 text-left">
                    <div class="col-md-3 mb-3">
                        <div class="font-weight-bold small text-dark">Guest</div>
                        <div class="mt-1">
                            {{guests.find(g => g.uuid === formOffer.guest_uuid)
                                ? `${guests.find(g => g.uuid === formOffer.guest_uuid).alias + '. ' || ''} ${guests.find(g=>g.uuid === formOffer.guest_uuid).fullname}`
                                : '-'
                            }}
                        </div>
                    </div>
                    <div class="col-md-3 mb-3">
                        <div class="font-weight-bold small text-dark">Offer Title / Name</div>
                        <div class="mt-1">{{ formOffer.name || '-' }}</div>
                    </div>

                    <div class="col-md-3 mb-3">
                        <div class="font-weight-bold small text-dark">Start Date (Arrival)</div>
                        <div class="mt-1">{{ formatDate(formOffer.start) }}</div>
                    </div>
                    <div class="col-md-3 mb-3">
                        <div class="font-weight-bold small text-dark">End Date (Departure)</div>
                        <div class="mt-1">{{ formatDate(formOffer.end) }}</div>
                    </div>
                    <div class="col-md-3 mb-3">
                        <div class="font-weight-bold small text-dark">Duration</div>
                        <div class="mt-1">{{ formOffer.duration_days }} Days</div>
                    </div>
                    <div class="col-md-3 mb-3">
                        <div class="font-weight-bold small text-dark">Pax</div>
                        <div class="mt-1">{{ formOffer.pax }}</div>
                    </div>

                    <div class="col-md-3 mb-2">
                        <div class="font-weight-bold small text-dark">Currency</div>
                        <div class="mt-1">{{ selectedCurrency?.name || '-' }} ({{ selectedCurrency?.symbol || '' }})
                        </div>
                    </div>
                    <div class="col-md-3 mb-2">
                        <div class="font-weight-bold small text-dark">Exchange Rate</div>
                        <div class="mt-1">{{ formatDecimal(formOffer.exchange_rate) }}</div>
                    </div>
                    <div class="col-md-3 mb-3">
                        <div class="font-weight-bold small text-dark">Status</div>
                        <div class="mt-1">
                            <BBadge :variant="statusBadgeVariant(formOffer.status)" class="text-capitalize">{{
                                formOffer.status
                            }}
                            </BBadge>
                        </div>
                    </div>
                </div>

                <!-- Program Data Table -->
                <div class="table-responsive">
                    <table class="table table-bordered table-sm">
                        <thead style="background-color: #e1eed9;">
                            <tr>
                                <th width="50">Day</th>
                                <th>Date</th>
                                <th>Name</th>
                                <th colspan="2">Destination</th>
                                <th>Accommodation</th>
                                <th>Transportation</th>
                                <th>Activity</th>
                                <th>Meal</th>
                                <th class="text-right" width="120">Sub Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(prog, idx) in formOffer.programs" :key="idx">
                                <td class="text-center font-weight-bold">{{ prog.day || (idx + 1) }}</td>
                                <td>{{ prog.date || '-' }}</td>
                                <td>{{ prog.name || '-' }}</td>
                                <td>{{destinations.find(d => d.uuid === prog.from_destination_uuid)?.name || '-'}}
                                </td>
                                <td>{{destinations.find(d => d.uuid === prog.to_destination_uuid)?.name || '-'}}</td>
                                <td>
                                    <ul class="mb-0 ps-3" v-if="prog.accommodations?.length">
                                        <li v-for="acc in prog.accommodations" :key="acc.name">
                                            {{ acc.name }}
                                            <BBadge v-if="acc.is_single_supplement" variant="info" class="ms-1 small">
                                                Single
                                                Supplement</BBadge>
                                        </li>
                                    </ul>
                                    <span v-else class="text-muted">-</span>
                                </td>
                                <td>
                                    <ul class="mb-0 ps-3" v-if="prog.transportations?.length">
                                        <li v-for="trans in prog.transportations" :key="trans.name">{{ trans.name }} Pax
                                            {{
                                                trans.pax }}</li>
                                    </ul>
                                    <span v-else class="text-muted">-</span>
                                </td>
                                <td>
                                    <ul class="mb-0 ps-3" v-if="prog.activities?.length">
                                        <li v-for="act in prog.activities" :key="act.name">{{ act.name }}</li>
                                    </ul>
                                    <span v-else class="text-muted">-</span>
                                </td>
                                <td>
                                    <ul class="mb-0 ps-3" v-if="prog.meals?.length">
                                        <li v-for="meal in prog.meals" :key="meal.name">{{ meal.name }}</li>
                                    </ul>
                                    <span v-else class="text-muted">-</span>
                                </td>
                                <td class="text-right font-weight-bold">
                                    {{ formatRupiah(getDaySubtotal(prog)) }}
                                    <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                        class="small text-muted italic">
                                        {{ formatCurrency(distributedForeign?.programs[index]?.total || 0) }}
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="!formOffer.programs?.length">
                                <td colspan="10" class="text-center text-muted py-4">No program data</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Fee Data Table -->
                <div class="table-responsive mb-0" style="margin-top: -1px;"
                    v-if="formOffer.entrance_fee > 0 || formOffer.ferry_fee > 0 || formOffer.toll_fee > 0 || formOffer.np_permit_fee > 0 || formOffer.single_supplement > 0 || calculatedAdditionalTotal > 0">
                    <table class="table table-bordered table-sm mb-0">
                        <thead style="background-color: #f8f9fa;">
                            <tr>
                                <th class="text-left font-weight-bold text-dark">Fees & Extras</th>
                                <th class="text-right text-dark" width="200">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="formOffer.entrance_fee > 0">
                                <td class="text-left italic small align-middle">Entrance Fee</td>
                                <td class="text-right font-weight-bold px-3">
                                    {{ formatRupiah((formOffer.pax || 1) * formOffer.entrance_fee) }}
                                    <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                        class="small text-muted italic">
                                        {{ formatCurrency(distributedForeign?.tourFees.entrance || 0) }}
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="formOffer.ferry_fee > 0">
                                <td class="text-left italic small align-middle">Ferry Fee</td>
                                <td class="text-right font-weight-bold px-3">
                                    {{ formatRupiah((formOffer.pax || 1) * formOffer.ferry_fee) }}
                                    <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                        class="small text-muted italic">
                                        {{ formatCurrency(distributedForeign?.tourFees.ferry || 0) }}
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="formOffer.toll_fee > 0">
                                <td class="text-left italic small align-middle">Toll Fee</td>
                                <td class="text-right font-weight-bold px-3">
                                    {{ formatRupiah((formOffer.pax || 1) * formOffer.toll_fee) }}
                                    <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                        class="small text-muted italic">
                                        {{ formatCurrency(distributedForeign?.tourFees.toll || 0) }}
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="formOffer.np_permit_fee > 0">
                                <td class="text-left italic small align-middle">NP Permit Fee</td>
                                <td class="text-right font-weight-bold px-3">
                                    {{ formatRupiah((formOffer.pax || 1) * formOffer.np_permit_fee) }}
                                    <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                        class="small text-muted italic">
                                        {{ formatCurrency(distributedForeign?.tourFees.npPermit || 0) }}
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="formOffer.single_supplement > 0">
                                <td class="text-left italic small align-middle">Single Supplement</td>
                                <td class="text-right font-weight-bold px-3">
                                    {{ formatRupiah(formOffer.single_supplement) }}
                                    <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                        class="small text-muted italic">
                                        {{ formatCurrency(distributedForeign?.singleSupplement || 0) }}
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="calculatedAdditionalTotal > 0">
                                <td class="text-left italic small align-middle">Additional Extras</td>
                                <td class="text-right font-weight-bold px-3">
                                    {{ formatRupiah(calculatedAdditionalTotal) }}
                                    <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                        class="small text-muted italic">
                                        {{ formatCurrency(distributedForeign?.additionals.reduce((a, b) => a + b, 0) || 0) }}
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Price Data Table -->
                <div class="table-responsive mb-4" style="margin-top: -1px;">
                    <table class="table table-bordered table-sm mb-0">
                        <thead style="background-color: #f8f9fa;">
                            <tr>
                                <th class="text-left font-weight-bold text-dark">Price Summary</th>
                                <th class="text-right text-dark" width="200">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td class="text-right text-muted italic font-weight-bold small align-middle">Subtotal:
                                </td>
                                <td class="text-right font-weight-bold px-3">
                                    {{ formatRupiah(calculatedSubtotal) }}
                                    <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                        class="small text-muted italic">
                                        {{ formatCurrency(distributedForeign?.summary.subtotal || 0) }}
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="formOffer.discount > 0">
                                <td class="text-right text-muted italic small align-middle">Discount ({{
                                    formOffer.discount
                                    }}%):</td>
                                <td class="text-right font-weight-bold px-3 text-danger">
                                    -{{ formatRupiah(calculatedDiscountAmount) }}
                                    <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                        class="small text-muted italic">
                                        -{{ formatCurrency(distributedForeign?.summary.discount || 0) }}
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="formOffer.service > 0">
                                <td class="text-right text-muted italic small align-middle">Service Charge ({{
                                    formOffer.service
                                    }}%):</td>
                                <td class="text-right font-weight-bold px-3">
                                    {{ formatRupiah(calculatedServiceAmount) }}
                                    <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                        class="small text-muted italic">
                                        {{ formatCurrency(distributedForeign?.summary.service || 0) }}
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="formOffer.tax > 0">
                                <td class="text-right text-muted italic small align-middle">Tax ({{ formOffer.tax }}%):
                                </td>
                                <td class="text-right font-weight-bold px-3">
                                    {{ formatRupiah(calculatedTaxAmount) }}
                                    <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                        class="small text-muted italic">
                                        {{ formatCurrency(distributedForeign?.summary.tax || 0) }}
                                    </div>
                                </td>
                            </tr>
                            <tr class="font-weight-bold">
                                <td class="text-right text-muted italic small align-middle">Grand Total Sales:</td>
                                <td class="text-right mb-0 px-3" style="background-color: #e1eed9;">
                                    {{ formatRupiah(formOffer.grand_total_final) }}
                                    <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                        class="small text-muted italic">
                                        {{ formatCurrency(distributedForeign?.summary.grandTotal || 0) }}
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="formOffer.deposit_amount > 0" class="italic small">
                                <td class="text-right text-muted align-middle">Deposit ({{ formOffer.deposit_percentage
                                    }}%):
                                </td>
                                <td class="text-right border-top px-3">
                                    {{ formatRupiah(formOffer.deposit_amount) }}
                                    <div v-if="selectedCurrency && selectedCurrency.code !== 'IDR'"
                                        class="small text-muted">
                                        {{ formatCurrency(distributedForeign?.deposit || 0) }}
                                    </div>
                                    <div v-if="formOffer.balance_due_date" class="x-small text-info">
                                        Due: {{ formatDate(formOffer.balance_due_date) }}
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Inclusions & Exclusions -->
                <div class="row mt-4">
                    <div class="col-6">
                        <h6 class="font-weight-bold border-bottom pb-2 mb-2">Inclusions</h6>
                        <div class="small" v-if="formOffer.inclusion">
                            <ul class="ps-3 mb-0">
                                <li v-for="(line, lidx) in formOffer.inclusion.split('\n').filter(l => l.trim())"
                                    :key="lidx">{{
                                        line
                                    }}</li>
                            </ul>
                        </div>
                        <span v-else class="text-muted small">-</span>
                    </div>
                    <div class="col-6">
                        <h6 class="font-weight-bold border-bottom pb-2 mb-2">Exclusions</h6>
                        <div class="small" v-if="formOffer.exclusion">
                            <ul class="ps-3 mb-0">
                                <li v-for="(line, lidx) in formOffer.exclusion.split('\n').filter(l => l.trim())"
                                    :key="lidx">{{
                                        line
                                    }}</li>
                            </ul>
                        </div>
                        <span v-else class="text-muted small">-</span>
                    </div>
                </div>

                <!-- Comments -->
                <div class="mt-4" v-if="formOffer.comments">
                    <h6 class="font-weight-bold border-bottom pb-2 mb-2">Comments</h6>
                    <p class="small mb-0" style="white-space: pre-wrap;">{{ formOffer.comments }}</p>
                </div>

                <!-- Passenger Info -->
                <div class="mt-4" v-if="formOffer.passanger_info">
                    <h6 class="font-weight-bold border-bottom pb-2 mb-2">Passenger/Luggage Capacity</h6>
                    <p class="small mb-0" style="white-space: pre-wrap;">{{ formOffer.passanger_info }}</p>
                </div>
            </div>
        </BModal>

        <!-- Additional Form Modal -->
        <AdditionalForm v-model:modal="additionalModal" :defaultPax="formOffer.pax" :selectedData="selectedAdditional"
            :exchangeRate="formOffer.exchange_rate" :currencyCode="selectedCurrency?.code"
            @addOrUpdate="handleAdditional" />
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, computed, inject, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useLoading } from 'vue-loading-overlay';
import { useAuthStore } from "@/store/auth.js";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required, minValue } from "@vuelidate/validators";
import { useToast } from "vue-toastification";
import axios from 'axios';
import HeaderContent from "@/components/HeaderContent.vue";
import { formatDate, formatRupiah, formatDecimal } from "@/utils/format";
import {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BFormSelect,
    BFormTextarea,
    BModal,
    BRow,
    BTab,
    BTabs,
} from "bootstrap-vue-next";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

// Import Modals
import TransportationForm from './TransportationForm.vue';
import AccommodationForm from './AccommodationForm.vue';
import ActivityForm from './ActivityForm.vue';
import MealForm from './MealForm.vue';
import AdditionalForm from "./AdditionalForm.vue";

const auth = useAuthStore();
const $loading = useLoading();
const swal = inject("$swal");
const toast = useToast();
const router = useRouter();
const route = useRoute();

const showPreview = ref(false);
const isSavingFlow = ref(false);
const submitted = ref(false);
const canAutoSave = ref(false);
const userInteracted = ref(false);
const isFirstAutosave = ref(true);

const debounce = (fn, delay) => {
    let timeoutId;
    const debounced = (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
    debounced.cancel = () => clearTimeout(timeoutId);
    return debounced;
};

const saveDraftToLocalStorage = (formData) => {
    if (isEdit.value || !formData) return;

    if (isFirstAutosave.value) {
        saveDraftDebounced10s(formData);
    } else {
        saveDraftDebounced5s(formData);
    }
};

const performAutosave = (formData) => {
    try {
        const dataString = JSON.stringify(formData);
        localStorage.setItem('offer_document_draft', dataString);
        console.log('Draft autosaved to LocalStorage:', formData);
        toast.info("Draft autosaved.", {
            timeout: 2000,
            position: "bottom-right"
        });
        isFirstAutosave.value = false;
    } catch (error) {
        console.error('Error saving draft', error);
    }
};

const saveDraftDebounced5s = debounce(performAutosave, 5000);
const saveDraftDebounced10s = debounce(performAutosave, 10000);

// const openPreview = () => {
//     isSavingFlow.value = false;
//     showPreview.value = true;
// };

// const printDocument = () => {
//     window.print();
// };

// const getDayDateShort = (index) => {
//     if (!formOffer.start) return '';
//     const date = new Date(formOffer.start);
//     date.setDate(date.getDate() + index);
//     return date.getDate() + '/' + (date.getMonth() + 1);
// };
//const toast = inject("$toast");

const isEdit = computed(() => !!route.params.uuid);
const statusBadgeVariant = (status) => {
    switch (status) {
        case 'Approved': return 'success';
        case 'Pending': return 'warning';
        case 'Rejected': return 'danger';
        case 'Completed': return 'primary';
        case 'Draft': return 'secondary';
        default: return 'info';
    }
};

// Loading States for Lazy Load
const loadingStates = reactive({
    guests: false,
    packages: false,
    destinations: false,
    masterPrograms: false,
    currencies: false,
    paymentMethods: false
});

const isLoaded = reactive({
    guests: false,
    packages: false,
    destinations: false,
    masterPrograms: false,
    currencies: false,
    paymentMethods: false
});

// Master Data
const guests = ref([]);
const packages = ref([]);
const destinations = ref([]);
const masterPrograms = ref([]);
const currencies = ref([]);
const paymentMethods = ref([]);
const selectedPackageUuid = ref("");

const loadGuests = async () => {
    if (isLoaded.guests) return;
    loadingStates.guests = true;
    try {
        const res = await axios.get(`${process.env.VUE_APP_API_URL}/guest`, { headers: { Authorization: "Bearer " + auth._token } });
        guests.value = res.data.data;
        isLoaded.guests = true;
    } catch (e) { console.error(e); }
    finally { loadingStates.guests = false; }
};

const loadPackages = async () => {
    if (isLoaded.packages) return;
    loadingStates.packages = true;
    try {
        const res = await axios.get(`${process.env.VUE_APP_API_URL}/package`, { headers: { Authorization: "Bearer " + auth._token } });
        packages.value = res.data.data;
        isLoaded.packages = true;
    } catch (e) { console.error(e); }
    finally { loadingStates.packages = false; }
};

const loadDestinations = async () => {
    if (isLoaded.destinations) return;
    loadingStates.destinations = true;
    try {
        const res = await axios.get(`${process.env.VUE_APP_API_URL}/destination`, { headers: { Authorization: "Bearer " + auth._token } });
        destinations.value = res.data.data;
        isLoaded.destinations = true;
    } catch (e) { console.error(e); }
    finally { loadingStates.destinations = false; }
};

const loadMasterPrograms = async () => {
    if (isLoaded.masterPrograms) return;
    loadingStates.masterPrograms = true;
    try {
        const res = await axios.get(`${process.env.VUE_APP_API_URL}/program`, { headers: { Authorization: "Bearer " + auth._token } });
        masterPrograms.value = res.data.data;
        isLoaded.masterPrograms = true;
    } catch (e) { console.error(e); }
    finally { loadingStates.masterPrograms = false; }
};

const loadCurrencies = async () => {
    if (isLoaded.currencies) return;
    loadingStates.currencies = true;
    try {
        const res = await axios.get(`${process.env.VUE_APP_API_URL}/currency`, { headers: { Authorization: "Bearer " + auth._token } });
        currencies.value = res.data.data;
        isLoaded.currencies = true;

        // After loading, if we need to set default IDR
        if (!formOffer.currency_uuid && !isEdit.value) {
            const idr = currencies.value.find(c => c.code === 'IDR');
            if (idr) formOffer.currency_uuid = idr.uuid;
        }
    } catch (e) { console.error(e); }
    finally { loadingStates.currencies = false; }
};

const loadPaymentMethods = async () => {
    if (isLoaded.paymentMethods) return;
    loadingStates.paymentMethods = true;
    try {
        const res = await axios.get(`${process.env.VUE_APP_API_URL}/payment-method`, { headers: { Authorization: "Bearer " + auth._token } });
        paymentMethods.value = res.data.data;
        isLoaded.paymentMethods = true;
    } catch (e) { console.error(e); }
    finally { loadingStates.paymentMethods = false; }
};

const statusOptions = [
    { value: 'Pending', text: 'Pending' },
    { value: 'Approved', text: 'Approved' },
    { value: 'Rejected', text: 'Rejected' },
    { value: 'Completed', text: 'Completed' }
];

// Modal State
const modals = reactive({
    transportation: false,
    accommodation: false,
    activity: false,
    meals: false
});
const selectedItem = ref({});
const activeDayIndex = ref(null);

// Fee Types State
const discountType = ref('percent');

// Main Form
const formOffer = reactive({
    guest_uuid: '',
    currency_uuid: '',
    payment_method_uuid: '',
    name: '',
    pax: 1,
    duration_days: 1,
    trans_date: '',
    start: '',
    end: '',
    status: 'Pending',
    exchange_rate: 1,
    price: 0,
    subtotal: 0,
    discount: 0,
    discount_amount: 0,
    tax: 0,
    tax_amount: 0,
    service: 0,
    service_amount: 0,
    additional_amount: 0,
    single_supplement: 0,
    entrance_fee: 0,
    ferry_fee: 0,
    toll_fee: 0,
    np_permit_fee: 0,
    grand_total: 0,
    grand_total_final: 0,
    grand_total_foreign: 0,
    inclusion: '',
    exclusion: '',
    comments: '',
    passanger_info: '',
    term_condition: `Prepayment for the tour is a fundamental part of the tour's income and contributes to the overall cost of equipment, running costs, staffing, administration, etc. As the Tri Jaya will have incurred the majority of its costs before the tour starts, we need to impose cancellation charges to cover these costs.

Prepayment is non-refundable. We encourage all Tri Jaya Guests to purchase Trip Cancellation Insurance. If you cancel your booking, we will impose cancellation charges as follows:
30 days or more prior to departure – loss of deposit
or fewer days before departure – 80% of tour cost
48 hours or fewer before departure – 100% of tour cost`,
    deposit_percentage: 0,
    deposit_amount: 0,
    deposit_amount_foreign: 0,
    balance_due_date: '',
    programs: [],
    additionals: [],
});

const rules = {
    formOffer: {
        guest_uuid: { required: helpers.withMessage("Guest is required", required) },
        currency_uuid: { required: helpers.withMessage("Currency is required", required) },
        payment_method_uuid: { required: helpers.withMessage("Payment method is required", required) },
        name: { required: helpers.withMessage("Offer Title is required", required) },
        pax: {
            required: helpers.withMessage("Pax is required", required),
            minValue: helpers.withMessage("Pax must be positive and not zero", minValue(1))
        },
        exchange_rate: {
            required: helpers.withMessage("Exchange rate is required", required),
            minValue: helpers.withMessage("Exchange rate must be positive and not zero", minValue(0.000001))
        },
        start: {},
        end: {},
        programs: {
            required,
            $each: helpers.forEach({
                from_destination_uuid: {
                    required: helpers.withMessage("From Destination is required", required)
                },
                to_destination_uuid: {
                    required: helpers.withMessage("To Destination is required", required)
                },
                name: {}
            })
        }
    }
};

const v$ = useVuelidate(rules, { formOffer }, { $scope: 'offerForm' });

onMounted(async () => {
    if (isEdit.value) {
        const loader = $loading.show();
        try {
            await loadOfferData(route.params.uuid);

            // For edit mode, we might want to pre-load the selected items' info 
            // so labels display correctly in v-select before user clicks them.
            // v-select handles this by showing the current label if provide it,
            // but usually we just load the whole list to be safe or rely on the API detail.
            // Let's load the essentials if we are in Edit mode.
            await Promise.all([
                loadGuests(),
                loadCurrencies(),
                loadPaymentMethods(),
                loadDestinations(),
                loadMasterPrograms()
            ]);
        } catch (e) {
            console.error(e);
        } finally {
            loader.hide();
        }
    } else {
        addDay();
        // For new, we can still load currencies to set IDR default
        loadCurrencies();
        loadPaymentMethods();

        // Check for draft
        const savedDraft = localStorage.getItem('offer_document_draft');
        if (savedDraft) {
            swal.fire({
                title: 'Draft Found',
                text: 'You have an unsaved offer draft. Would you like to continue?',
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: 'Yes, Continue Draft',
                cancelButtonText: 'Cancel, Start New',
                reverseButtons: true,
                allowOutsideClick: false,
                allowEscapeKey: false
            }).then(async (result) => {
                if (result.isConfirmed) {
                    try {
                        const parsedDraft = JSON.parse(savedDraft);
                        Object.assign(formOffer, parsedDraft);

                        // Load all master data so selections show correct labels
                        const loader = $loading.show();
                        await Promise.all([
                            loadGuests(),
                            loadCurrencies(),
                            loadPaymentMethods(),
                            loadDestinations(),
                            loadMasterPrograms()
                        ]);
                        loader.hide();

                        toast.success("Draft loaded successfully.");
                    } catch (e) {
                        console.error("Error parsing draft", e);
                        toast.error("Failed to load draft.");
                    }
                } else {
                    localStorage.removeItem('offer_document_draft');
                }

                // Enable autosave after handling the prompt
                setTimeout(() => {
                    canAutoSave.value = true;
                }, 1000);
            });
        } else {
            canAutoSave.value = true;
        }
    }
});

onUnmounted(() => {
    saveDraftDebounced5s.cancel();
    saveDraftDebounced10s.cancel();
});

const calculateDuration = () => {
    if (formOffer.start && formOffer.end) {
        const start = new Date(formOffer.start);
        const end = new Date(formOffer.end);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        formOffer.duration_days = diffDays > 0 ? diffDays : 1;
    } else {
        formOffer.duration_days = formOffer.programs ? Math.max(formOffer.programs.length, 1) : 1;
    }
};

watch(() => formOffer.start, (newStart) => {
    if (newStart) {
        formOffer.programs.forEach((prog, index) => {
            const d = new Date(newStart);
            d.setDate(d.getDate() + index);
            prog.date = d.toISOString().split('T')[0];
        });
    }
});

const isApprovedOrPaid = computed(() => {
    const status = formOffer.status?.toLowerCase();
    return status === 'approved' || status === 'completed';
});

watch(() => formOffer.currency_uuid, (newUuid) => {
    const currency = currencies.value.find(c => c.uuid === newUuid);
    if (currency) {
        if (currency.code === 'IDR') {
            formOffer.exchange_rate = 1;
        } else if (isEdit.value && formOffer.currency_uuid === newUuid) {
            // If editing and same currency, keep current rate from DB
        } else {
            // Suggest 1 or ideally we'd fetch latest rate, but for now 1 is safer than keeping IDR rate
            // User will be prompted to check exchange rate.
            if (formOffer.exchange_rate === 1) {
                // If it was IDR, reset to 0 or some default to force user input
                formOffer.exchange_rate = 0;
            }
        }
    }
});



// Helper to ensure unique lines in inclusion/exclusion
const ensureUniqueLines = (text) => {
    if (!text) return '';
    const lines = text.split('\n');
    const seen = new Set();
    const uniqueLines = lines.filter(line => {
        const trimmed = line.trim().toLowerCase();
        if (trimmed === '' || seen.has(trimmed)) return false;
        seen.add(trimmed);
        return true;
    });
    return uniqueLines.join('\n');
};

// Auto-cleanup inclusion/exclusion on change
watch(() => formOffer.inclusion, (newVal) => {
    const cleaned = ensureUniqueLines(newVal);
    if (cleaned !== newVal) {
        formOffer.inclusion = cleaned;
    }
});

watch(() => formOffer.exclusion, (newVal) => {
    const cleaned = ensureUniqueLines(newVal);
    if (cleaned !== newVal) {
        formOffer.exclusion = cleaned;
    }
});



const updatePassengerInfoTemplate = () => {
    // Collect all transportations across all programs
    const allTrans = formOffer.programs.flatMap(p => p.transportations || []);

    // Get unique vehicles that have capacity info
    const uniqueVehicles = [];
    const seenNames = new Set();

    allTrans.forEach(t => {
        const vehicle = t.vehicle;
        if (vehicle && vehicle.name && !seenNames.has(vehicle.name)) {
            // Check if it has the capacity data
            if (vehicle.seat_capacity !== undefined || vehicle.luggage_capacity !== undefined) {
                uniqueVehicles.push(vehicle);
                seenNames.add(vehicle.name);
            }
        }
    });

    if (uniqueVehicles.length === 0) return;

    let template = "To ensure a comfortable tour experience, please refer to the following luggage guide:\n";

    uniqueVehicles.forEach(v => {
        template += `${v.name}, Maximum seating capacity is ${v.seat_capacity || 0} passengers, plus the driver. Maximum luggage capacity for a ${v.name} is ${v.luggage_capacity || 0} large suitcases or checked in baggage.\n`;
    });

    formOffer.passanger_info = template.trim();
};

const updateAccommodationInclusions = () => {
    let nights = 0;
    formOffer.programs.forEach(p => {
        if (p.accommodations && p.accommodations.length > 0) {
            nights++;
        }
    });

    let currentText = formOffer.inclusion || '';
    let lines = currentText.split('\n');

    // Remove old auto-generated lines to prevent duplicates
    // We look for flexible matches
    lines = lines.filter(l => {
        const lower = l.toLowerCase();
        return !lower.includes('night in above mentioned hotels') && !lower.includes('nights in above mentioned hotels') && !lower.includes('daily breakfast');
    });

    // If we have nights with accommodation, add the lines back at the top
    if (nights > 0) {
        // Add Daily Breakfast
        lines.unshift('Daily breakfast');
        // Add Nights count
        lines.unshift(`${nights} ${nights === 1 ? 'night' : 'nights'} in above mentioned hotels`);
    }

    // Join back
    const newText = lines.join('\n');

    // Only update if changed to avoid infinite loop with the inclusion watcher
    if (newText !== currentText) {
        formOffer.inclusion = newText;
    }
};

// Auto-update Passenger Info template and Inclusions when programs change
watch(
    () => formOffer.programs,
    () => {
        updatePassengerInfoTemplate();
        updateAccommodationInclusions();
    },
    { deep: true }
);

const getDayDate = (index) => {
    if (!formOffer.start) return '';
    const date = new Date(formOffer.start);
    date.setDate(date.getDate() + index);
    return formatDate(date);
};

const loadPreset = async () => {
    if (!selectedPackageUuid.value) return;
    const confirm = await swal.fire({
        title: 'Load Package?',
        text: 'This will replace your current program with this package data.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, Load'
    });

    if (confirm.isConfirmed) {
        const loader = $loading.show();
        try {
            const response = await axios.get(`${process.env.VUE_APP_API_URL}/package/detail/${selectedPackageUuid.value}`, {
                headers: { Authorization: "Bearer " + auth._token }
            });
            const pkg = response.data.data;
            formOffer.name = pkg.name;
            formOffer.pax = pkg.pax;
            formOffer.duration_days = pkg.duration_days;
            formOffer.inclusion = pkg.inclusion;
            formOffer.exclusion = pkg.exclusion;
            if (pkg.programs) {
                // Populate destinations from package if they exist to show labels immediately (list sementara)
                pkg.programs.forEach(p => {
                    if (p.from_destination && !destinations.value.find(d => d.uuid === p.from_destination.uuid)) {
                        destinations.value.push(p.from_destination);
                    }
                    if (p.to_destination && !destinations.value.find(d => d.uuid === p.to_destination.uuid)) {
                        destinations.value.push(p.to_destination);
                    }
                });

                formOffer.programs = pkg.programs.map(p => ({
                    day: p.day,
                    date: p.date,
                    name: p.name || '',
                    pax: p.pax,
                    price: p.price || 0,
                    from_destination_uuid: p.from_destination_uuid || null,
                    to_destination_uuid: p.to_destination_uuid || null,
                    transportations: p.transportations ? [...p.transportations] : [],
                    accommodations: p.accommodations ? [...p.accommodations] : [],
                    activities: p.activities ? [...p.activities] : [],
                    meals: p.meals ? [...p.meals] : [],
                }));
            }
        } catch (e) {
            console.error(e);
        } finally {
            loader.hide();
        }
    }
};

const addDay = () => {
    userInteracted.value = true;
    const index = formOffer.programs.length;
    let autoDate = '';
    if (formOffer.start) {
        const d = new Date(formOffer.start);
        d.setDate(d.getDate() + index);
        autoDate = d.toISOString().split('T')[0];
    }

    formOffer.programs.push({
        day: index + 1,
        date: autoDate,
        name: '',
        price: 0,
        from_destination_uuid: null,
        to_destination_uuid: null,
        transportations: [],
        accommodations: [],
        activities: [],
        meals: [],
        save_as_master: false
    });
    calculateDuration();
};

const removeDay = async (index) => {
    userInteracted.value = true;
    // Check if this is the last program
    if (formOffer.programs.length === 1) {
        resetForm();
        return;
    }

    // If not the last program, remove normally
    formOffer.programs.splice(index, 1);
    formOffer.programs.forEach((p, idx) => p.day = idx + 1);
    calculateDuration();
};

const resetForm = () => {
    // Reset only programs array
    formOffer.programs = [];

    // Add one empty program
    addDay();
};

const getFilteredPrograms = (program) => {
    if (!program.from_destination_uuid || !program.to_destination_uuid) return [];

    const filtered = masterPrograms.value.filter(p =>
        p.from_destination_uuid === program.from_destination_uuid &&
        p.to_destination_uuid === program.to_destination_uuid
    );

    // Filter by unique name
    const uniqueMap = new Map();
    filtered.forEach(p => {
        const cleanName = p.name ? p.name.replace(/(?:\s*\(\d+\s*Pax\))+$/i, '') : '';
        if (!uniqueMap.has(cleanName)) {
            uniqueMap.set(cleanName, { ...p, name: cleanName });
        }
    });

    return Array.from(uniqueMap.values());
};

const selectProgramForDay = async (dayIndex, programNameOrObj) => {
    if (!programNameOrObj) return;

    // If it's a string from taggable, just set name
    if (typeof programNameOrObj === 'string') {
        formOffer.programs[dayIndex].name = programNameOrObj;
        return;
    }

    // If it's an object from master, load its components
    const master = programNameOrObj;
    if (!master || !master.uuid) return;

    formOffer.programs[dayIndex].name = master.name;

    // Fetch full master program details to get its sub-items
    const loader = $loading.show();
    try {
        const res = await axios.get(`${process.env.VUE_APP_API_URL}/program/detail/${master.uuid}`, {
            headers: { Authorization: "Bearer " + auth._token }
        });
        const details = res.data.data;

        // Debug: Check what data we're getting
        console.log('Program details from API:', details);
        console.log('Inclusion:', details.inclusion);
        console.log('Exclusion:', details.exclusion);

        // Populate items
        formOffer.programs[dayIndex].transportations = details.transportation?.map(t => ({ ...t, uuid: null })) || [];
        formOffer.programs[dayIndex].accommodations = details.accommodation?.map(a => ({ ...a, uuid: null })) || [];
        formOffer.programs[dayIndex].activities = details.activity?.map(a => ({ ...a, uuid: null })) || [];
        formOffer.programs[dayIndex].meals = details.meals?.map(m => ({ ...m, uuid: null, pax: formOffer.pax })) || [];

        // Append inclusions - check line by line to avoid duplicates
        if (details.inclusion) {
            const existingInclusions = formOffer.inclusion ? formOffer.inclusion.split('\n').map(line => line.trim()).filter(line => line) : [];
            const newInclusions = details.inclusion.split('\n').map(line => line.trim()).filter(line => line);

            newInclusions.forEach(newLine => {
                if (!existingInclusions.includes(newLine)) {
                    formOffer.inclusion = (formOffer.inclusion ? formOffer.inclusion + '\n' : '') + newLine;
                    existingInclusions.push(newLine);
                }
            });
        }

        // Append exclusions - check line by line to avoid duplicates
        if (details.exclusion) {
            const existingExclusions = formOffer.exclusion ? formOffer.exclusion.split('\n').map(line => line.trim()).filter(line => line) : [];
            const newExclusions = details.exclusion.split('\n').map(line => line.trim()).filter(line => line);

            newExclusions.forEach(newLine => {
                if (!existingExclusions.includes(newLine)) {
                    formOffer.exclusion = (formOffer.exclusion ? formOffer.exclusion + '\n' : '') + newLine;
                    existingExclusions.push(newLine);
                }
            });
        }

    } catch (e) {
        console.error(e);
        toast.error("Failed to load master program components");
    } finally {
        loader.hide();
    }
};


const openModalAdd = (type, dayIndex) => {
    activeDayIndex.value = dayIndex;
    selectedItem.value = {};
    modals[type] = true;
};

const openModalEdit = (payload) => {
    activeDayIndex.value = payload.dayIndex;
    selectedItem.value = { ...payload.item, index: payload.itemIndex };
    modals[payload.type] = true;
};

const handleSaveItem = (type, payload) => {
    userInteracted.value = true;
    const day = formOffer.programs[activeDayIndex.value];
    if (payload.index !== undefined) {
        const idx = payload.index;
        const data = { ...payload };
        delete data.index;
        day[type][idx] = data;
    } else {
        day[type].push(payload);
    }

    // Force recalculate day generic price based on components to keep it in sync
    // This assumes 'price' on the program object is intended to be the sum of its parts
    // OR if there is a manual base price, we preserve it? 
    // Based on user complaint "calculations wrong", usually means parts != total.
    // Let's rely on the computed properties which use getDaySubtotal.
    // However, getDaySubtotal adds day.price + components. 
    // If day.price was set from API (which stores the sum), and then we add components to it again, it doubles.
    // FIX: We should reset day.price to 0 if we are summing components, OR ensure day.price is ONLY used for "other fees".
    // For this context, let's assume day.price is the stored sum from DB. When we edit components locally, 
    // we should NOT use day.price in the sum, OR we should update day.price to be the new sum.

    // Let's update day.price to be the sum of components so that `getDaySubtotal` which uses it (and might double count if we aren't careful) works expectedly.
    // ACTUALLY, looking at getDaySubtotal:
    // total = Number(day.price || 0) + (trans) + (accom) + ...
    // This implies day.price is an ADDITIONAL base price. 
    // BUT when loading from API, day.price is the total stored in DB.
    // This is the bug. 
    // When data is loaded, day.price = X. Then getDaySubtotal adds components Y. Result = X + Y. Double counting!

    // Fix: When an item is added/updated, we should probably NOT touch day.price here but fix getDaySubtotal.
    // But since I can only edit this function block easily:
    // I will set day.price = 0 here to prevent double counting effectively "resetting" the base calculation to be component-based.
    day.price = 0;

    // Also handle inclusion/exclusion from items if they exist
    if (payload.inclusion) {
        const currentLines = formOffer.inclusion ? formOffer.inclusion.split('\n').map(l => l.trim().toLowerCase()) : [];
        const newLines = payload.inclusion.split('\n').filter(line => {
            const trimmedLine = line.trim();
            return trimmedLine !== '' && !currentLines.includes(trimmedLine.toLowerCase());
        });
        if (newLines.length > 0) {
            formOffer.inclusion += (formOffer.inclusion ? '\n' : '') + newLines.join('\n');
        }
    }
    if (payload.exclusion) {
        const currentLines = formOffer.exclusion ? formOffer.exclusion.split('\n').map(l => l.trim().toLowerCase()) : [];
        const newLines = payload.exclusion.split('\n').filter(line => {
            const trimmedLine = line.trim();
            return trimmedLine !== '' && !currentLines.includes(trimmedLine.toLowerCase());
        });
        if (newLines.length > 0) {
            formOffer.exclusion += (formOffer.exclusion ? '\n' : '') + newLines.join('\n');
        }
    }
};

const handleSaveTransportation = (payload) => {
    userInteracted.value = true;
    if (payload.applyToAllDays) {
        // Apply to all days
        const transportData = { ...payload };
        delete transportData.applyToAllDays; // Remove flag
        delete transportData.index; // Remove index if exists

        formOffer.programs.forEach((program) => {
            if (!program.transportations) {
                program.transportations = [];
            }
            program.transportations.push({ ...transportData });
            // Reset base price to avoid double counting
            program.price = 0;
        });

        // toast.success(`Transportation added to all ${formOffer.programs.length} days`);

        // Sync inclusions/exclusions from first occurrence
        if (transportData.inclusion) {
            const currentLines = formOffer.inclusion ? formOffer.inclusion.split('\n').map(l => l.trim().toLowerCase()) : [];
            const newLines = transportData.inclusion.split('\n').filter(line => {
                const trimmedLine = line.trim();
                return trimmedLine !== '' && !currentLines.includes(trimmedLine.toLowerCase());
            });
            if (newLines.length > 0) {
                formOffer.inclusion += (formOffer.inclusion ? '\n' : '') + newLines.join('\n');
            }
        }

        if (transportData.exclusion) {
            const currentLines = formOffer.exclusion ? formOffer.exclusion.split('\n').map(l => l.trim().toLowerCase()) : [];
            const newLines = transportData.exclusion.split('\n').filter(line => {
                const trimmedLine = line.trim();
                return trimmedLine !== '' && !currentLines.includes(trimmedLine.toLowerCase());
            });
            if (newLines.length > 0) {
                formOffer.exclusion += (formOffer.exclusion ? '\n' : '') + newLines.join('\n');
            }
        }
    } else {
        // Add to current day only (use existing logic)
        handleSaveItem('transportations', payload);
    }
};

const removeDayItem = (type, dayIndex, itemIndex) => {
    userInteracted.value = true;
    formOffer.programs[dayIndex][type].splice(itemIndex, 1);
    // Reset base price to avoid double counting from API loaded data
    formOffer.programs[dayIndex].price = 0;
};


// Calculations
const distributeForeign = (idrArray, totalIdr, exchangeRate, forcedTarget = null) => {
    if (!exchangeRate || exchangeRate <= 0) return idrArray?.map(() => 0) || [];
    if (!idrArray || idrArray.length === 0) return [];

    const targetTotalForeign = forcedTarget !== null ? forcedTarget : Math.round(totalIdr / exchangeRate);

    // If only one item, it must match the target
    if (idrArray.length === 1) return [targetTotalForeign];

    let currentSum = 0;
    const lastNonZeroIndex = [...idrArray].reverse().findIndex(val => val !== 0);
    const finalIndex = lastNonZeroIndex !== -1 ? idrArray.length - 1 - lastNonZeroIndex : idrArray.length - 1;

    const result = idrArray.map((idr, idx) => {
        if (idx === finalIndex) {
            return targetTotalForeign - currentSum;
        }
        const val = Math.round(idr / exchangeRate);
        currentSum += val;
        return val;
    });
    
    // Ensure we fill the rest of the array with 0 if finalIndex was not the last item
    // (though map handles this, just being careful about logic)

    return result;
};

const distributedForeign = computed(() => {
    const rate = Number(formOffer.exchange_rate || 1);
    if (rate <= 0) return null;

    // 1. Calculate IDR components
    const dayTotalsIdr = formOffer.programs.map(getDaySubtotal);
    const additionalsIdr = formOffer.additionals.map(a => Number(a.sub_total || (a.price * a.pax)));
    
    const entranceTotalIdr = Number(formOffer.entrance_fee || 0) * Number(formOffer.pax || 1);
    const ferryTotalIdr = Number(formOffer.ferry_fee || 0) * Number(formOffer.pax || 1);
    const tollTotalIdr = Number(formOffer.toll_fee || 0) * Number(formOffer.pax || 1);
    const npPermitTotalIdr = Number(formOffer.np_permit_fee || 0) * Number(formOffer.pax || 1);
    const tourFeesIdrList = [entranceTotalIdr, ferryTotalIdr, tollTotalIdr, npPermitTotalIdr];

    const singleSupplementIdr = Number(formOffer.single_supplement || 0);

    // Total Subtotal IDR
    const subtotalIdr = calculatedSubtotal.value;

    // 2. Distribute at Top Level
    const topLevelIdrList = [
        dayTotalsIdr.reduce((a, b) => a + b, 0),
        additionalsIdr.reduce((a, b) => a + b, 0),
        tourFeesIdrList.reduce((a, b) => a + b, 0),
        singleSupplementIdr
    ];
    const topLevelForeign = distributeForeign(topLevelIdrList, subtotalIdr, rate);

    const [foreignProgramsTotal, foreignAdditionalsTotal, foreignTourFeesTotal, foreignSingleSupplement] = topLevelForeign;

    // 3. Distribute within Programs
    const foreignProgramsList = distributeForeign(dayTotalsIdr, topLevelIdrList[0], rate, foreignProgramsTotal);
    
    const programsDetailed = formOffer.programs.map((day, idx) => {
        const dayIdrTotal = dayTotalsIdr[idx];
        const dayForeignTotal = foreignProgramsList[idx];

        const transIdr = day.transportations?.map(i => Number(i.sub_total || (i.price * i.pax))) || [];
        const accomIdr = day.accommodations?.filter(i => !i.is_single_supplement).map(i => Number(i.total_price || i.sub_total || (i.price * i.pax))) || [];
        const actIdr = day.activities?.map(i => Number(i.sub_total || (i.price * i.pax))) || [];
        const mealIdr = day.meals?.map(i => Number(i.sub_total || (i.price * (i.pax || formOffer.pax)))) || [];
        
        const allItemsIdr = [...transIdr, ...accomIdr, ...actIdr, ...mealIdr];
        const allItemsForeign = distributeForeign(allItemsIdr, dayIdrTotal, rate, dayForeignTotal);

        let offset = 0;
        const transForeign = allItemsForeign.slice(offset, offset + transIdr.length);
        offset += transIdr.length;
        const accomForeign = allItemsForeign.slice(offset, offset + accomIdr.length);
        offset += accomIdr.length;
        const actForeign = allItemsForeign.slice(offset, offset + actIdr.length);
        offset += actIdr.length;
        const mealForeign = allItemsForeign.slice(offset, offset + mealIdr.length);

        return {
            total: dayForeignTotal,
            transportations: transForeign,
            accommodations: accomForeign,
            activities: actForeign,
            meals: mealForeign
        };
    });

    // 4. Distribute Additionals
    const foreignAdditionalsList = distributeForeign(additionalsIdr, topLevelIdrList[1], rate, foreignAdditionalsTotal);

    // 5. Distribute Tour Fees
    const foreignTourFeesList = distributeForeign(tourFeesIdrList, topLevelIdrList[2], rate, foreignTourFeesTotal);

    // 6. Final Summary Distribution
    const discountIdr = Number(calculatedDiscountAmount.value || 0);
    const serviceIdr = Number(calculatedServiceAmount.value || 0);
    const taxIdr = Number(calculatedTaxAmount.value || 0);
    const finalTotalIdr = Number(finalTotal.value || 0);

    // Use grand_total_final if it's set and non-zero (always prioritize manual sales override)
    const manualSalesIdr = Number(formOffer.grand_total_final || 0);
    const effectiveGrandTotalIdr = (manualSalesIdr !== 0) ? manualSalesIdr : finalTotalIdr;
    
    // Use manual foreign total if set, otherwise calculate from rate
    const manualForeignTotal = Number(formOffer.grand_total_foreign || 0);
    const grandTotalForeign = (manualForeignTotal !== 0) ? Math.round(manualForeignTotal) : Math.round(effectiveGrandTotalIdr / rate);
    
    // summaryIdrList: [Subtotal, -Discount, Service, Tax]
    // We adjust the summary to match the effectiveGrandTotalIdr
    const summaryIdrList = [subtotalIdr, -discountIdr, serviceIdr, taxIdr];
    const summaryForeign = distributeForeign(summaryIdrList, effectiveGrandTotalIdr, rate, grandTotalForeign);
    
    // Calculate deposit as percentage of foreign grand total for perfect consistency
    // This avoids IDR→Foreign reconversion rounding errors
    const depPct = Number(formOffer.deposit_percentage || 0) / 100;
    const depositForeign = Math.round(grandTotalForeign * depPct);
    const outstandingForeign = grandTotalForeign - depositForeign;

    return {
        programs: programsDetailed,
        additionals: foreignAdditionalsList,
        tourFees: {
            entrance: foreignTourFeesList[0],
            ferry: foreignTourFeesList[1],
            toll: foreignTourFeesList[2],
            npPermit: foreignTourFeesList[3]
        },
        singleSupplement: foreignSingleSupplement,
        deposit: depositForeign,
        outstanding: outstandingForeign,
        summary: {
            subtotal: Math.round(summaryForeign[0] || 0),
            discount: -Math.round(summaryForeign[1] || 0),
            service: Math.round(summaryForeign[2] || 0),
            tax: Math.round(summaryForeign[3] || 0),
            deposit: depositForeign,
            outstanding: outstandingForeign,
            grandTotal: Math.round(grandTotalForeign)
        }
    };
});

const getDaySubtotal = (day) => {
    let total = 0;

    total += (day.transportations?.reduce((s, i) => s + Number(i.sub_total || (i.price * i.pax)), 0) || 0);
    // Exclude single supplement items from day subtotal (base price)
    total += (day.accommodations?.filter(i => !i.is_single_supplement).reduce((s, i) => s + Number(i.total_price || i.sub_total || (i.price * i.pax)), 0) || 0);
    total += (day.activities?.reduce((s, i) => s + Number(i.sub_total || (i.price * i.pax)), 0) || 0);
    total += (day.meals?.reduce((s, i) => s + Number(i.sub_total || (i.price * (i.pax || formOffer.pax))), 0) || 0);

    return total;
};

const autoSingleSupplement = computed(() => {
    let sum = 0;
    formOffer.programs.forEach(day => {
        sum += (day.accommodations?.filter(i => i.is_single_supplement).reduce((s, i) => s + Number(i.total_price || i.sub_total || (i.price * i.pax)), 0) || 0);
    });
    return sum;
});

watch(autoSingleSupplement, (newVal) => {
    formOffer.single_supplement = newVal;
});

const programSubtotal = computed(() => {
    let total = 0;
    formOffer.programs.forEach(day => {
        total += getDaySubtotal(day);
    });
    return total;
});



const calculatedAdditionalTotal = computed(() => {
    return formOffer.additionals.reduce((s, i) => s + Number(i.sub_total || (i.price * i.pax)), 0) || 0;
});

watch(calculatedAdditionalTotal, (newVal) => {
    formOffer.additional_amount = newVal;
});

const calculatedPricePerPax = computed(() => {
    if (!formOffer.pax || formOffer.pax <= 0) return 0;
    return Math.round((calculatedSubtotal.value || 0) / formOffer.pax);
});

// const foreignPricePerPax = computed(() => {
//     return calculatedPricePerPax.value / Number(formOffer.exchange_rate || 1);
// });


// -- Additionals Management
const additionalModal = ref(false);
const selectedAdditional = ref({});

const openAdditionalModal = (item, index) => {
    selectedAdditional.value = item ? { ...item, index } : {};
    additionalModal.value = true;
};

const handleAdditional = (payload) => {
    userInteracted.value = true;
    if (payload.index !== undefined) {
        // Update
        formOffer.additionals[payload.index] = { ...payload };
    } else {
        // Create
        formOffer.additionals.push({ ...payload });
    }

    if (payload.inclusion) {
        const currentLines = formOffer.inclusion ? formOffer.inclusion.split('\n').map(l => l.trim().toLowerCase()) : [];
        const newLines = payload.inclusion.split('\n').filter(line => {
            const trimmedLine = line.trim();
            return trimmedLine !== '' && !currentLines.includes(trimmedLine.toLowerCase());
        });
        if (newLines.length > 0) {
            formOffer.inclusion += (formOffer.inclusion ? '\n' : '') + newLines.join('\n');
        }
    }
    if (payload.exclusion) {
        const currentLines = formOffer.exclusion ? formOffer.exclusion.split('\n').map(l => l.trim().toLowerCase()) : [];
        const newLines = payload.exclusion.split('\n').filter(line => {
            const trimmedLine = line.trim();
            return trimmedLine !== '' && !currentLines.includes(trimmedLine.toLowerCase());
        });
        if (newLines.length > 0) {
            formOffer.exclusion += (formOffer.exclusion ? '\n' : '') + newLines.join('\n');
        }
    }

    additionalModal.value = false;
};

const removeAdditional = (index) => {
    userInteracted.value = true;
    formOffer.additionals.splice(index, 1);
};
// -- End Additionals Management

const calculatedTourFees = computed(() => {
    const pax = Number(formOffer.pax || 1);
    const entrance = Number(formOffer.entrance_fee || 0);
    const ferry = Number(formOffer.ferry_fee || 0);
    const toll = Number(formOffer.toll_fee || 0);
    const npPermit = Number(formOffer.np_permit_fee || 0);
    return (entrance + ferry + toll + npPermit) * pax;
});

const calculatedSubtotal = computed(() => {
    let total = 0;
    const additional = Number(calculatedAdditionalTotal.value || 0);
    const single = Number(formOffer.single_supplement || 0);
    const program = Number(programSubtotal.value || 0);
    const tourFees = Number(calculatedTourFees.value || 0);
    total = additional + single + program + tourFees;
    return Math.round(total);
});

// const foreignSubtotal = computed(() => {
//     return distributedForeign.value?.summary.subtotal || 0;
// });

// Transportation total across all programs
const transportationTotal = computed(() => {
    let total = 0;
    formOffer.programs.forEach(day => {
        total += (day.transportations?.reduce((s, i) => s + Number(i.sub_total || (i.price * i.pax)), 0) || 0);
    });
    return total;
});

const taxableBase = computed(() => {
    return Math.max(0, calculatedSubtotal.value - transportationTotal.value);
});

const calculatedTaxAmount = computed(() => {
    return Math.round(taxableBase.value * (Number(formOffer.tax || 0) / 100));
});

const calculatedServiceAmount = computed(() => {
    return Math.round(taxableBase.value * (Number(formOffer.service || 0) / 100));
});

const calculatedDiscountAmount = computed(() => {
    if (discountType.value === 'nominal') return Number(formOffer.discount_amount || 0);
    return Math.round(calculatedSubtotal.value * (Number(formOffer.discount || 0) / 100));
});

const syncDiscountToNominal = () => {
    formOffer.discount_amount = Math.round((calculatedSubtotal.value || 0) * (Number(formOffer.discount || 0) / 100));
};

const syncDiscountToPercent = () => {
    const sub = calculatedSubtotal.value || 0;
    if (sub > 0) {
        formOffer.discount = Number(((Number(formOffer.discount_amount || 0) / sub) * 100).toFixed(2));
    } else {
        formOffer.discount = 0;
    }
};

const handleTypeChange = () => {
    if (discountType.value === 'percent') {
        syncDiscountToNominal();
    } else {
        syncDiscountToPercent();
    }
};

// watch(() => formOffer.discount, () => {
//     if (discountType.value === 'percent') {
//         syncDiscountToNominal();
//     }
// });

// watch(() => formOffer.discount_amount, () => {
//     if (discountType.value === 'nominal') {
//         syncDiscountToPercent();
//     }
// });

// const foreignDiscountAmount = computed(() => {
//     return distributedForeign.value?.summary.discount || 0;
// });

const finalTotal = computed(() => {
    const subTotal = calculatedSubtotal.value || 0;
    const taxService = (calculatedTaxAmount.value || 0) + (calculatedServiceAmount.value || 0);
    const discount = calculatedDiscountAmount.value || 0;

    return Math.round(subTotal + taxService - discount);
});

watch([() => formOffer.deposit_percentage, finalTotal, () => formOffer.grand_total_final, () => formOffer.grand_total_foreign], ([percent]) => {
    const manualSalesIdr = Number(formOffer.grand_total_final || 0);
    const effectiveTotalIdr = (manualSalesIdr !== 0) ? manualSalesIdr : finalTotal.value;
    
    const rate = Number(formOffer.exchange_rate || 1);
    const manualForeignTotal = Number(formOffer.grand_total_foreign || 0);
    const effectiveTotalForeign = (manualForeignTotal !== 0) ? manualForeignTotal : Math.round(effectiveTotalIdr / (rate > 0 ? rate : 1));

    if (percent > 0) {
        formOffer.deposit_amount = Math.round(effectiveTotalIdr * (Number(percent) / 100));
        formOffer.deposit_amount_foreign = Math.round(effectiveTotalForeign * (Number(percent) / 100));
    } else {
        formOffer.deposit_amount = 0;
        formOffer.deposit_amount_foreign = 0;
    }
});

// Watch only title, programs, and pricing for autosave (5s debounce, 10s initial)
watch([
    () => formOffer.name,
    () => formOffer.programs,
    calculatedSubtotal,
    finalTotal,
], () => {
    if (!canAutoSave.value || !userInteracted.value) return;
    saveDraftToLocalStorage(formOffer);
}, { deep: true });

// const calculatedGrandTotalPerPax = computed(() => {
//     if (!formOffer.pax || formOffer.pax <= 0) return 0;
//     return finalTotal.value / formOffer.pax;
// });

/*const foreignGrandTotalPerPax = computed(() => {
    return calculatedGrandTotalPerPax.value / Number(formOffer.exchange_rate || 1);
});*/


// const foreignFinalTotal = computed(() => {
//     return distributedForeign.value?.summary.grandTotal || 0;
// });

const foreignCalculatedTotal = computed(() => {
    const rate = Number(formOffer.exchange_rate || 1);
    if (rate <= 0) return 0;
    return Math.round(finalTotal.value / rate);
});

// -- Synchronize calculated values to formOffer for API persistence
watch(calculatedSubtotal, (newVal) => {
    if (formOffer.subtotal !== newVal) {
        formOffer.subtotal = newVal;
    }
    if (discountType.value === 'percent') syncDiscountToNominal();
    else syncDiscountToPercent();
});

watch(calculatedTaxAmount, (newVal) => {
    if (formOffer.tax_amount !== newVal) {
        formOffer.tax_amount = newVal;
    }
});

watch(calculatedServiceAmount, (newVal) => {
    if (formOffer.service_amount !== newVal) {
        formOffer.service_amount = newVal;
    }
});

watch(calculatedDiscountAmount, (newVal) => {
    if (formOffer.discount_amount !== newVal) {
        formOffer.discount_amount = newVal;
    }
});

const onManualIdrChange = (newVal) => {
    // Sales IDR is now a purely independent manual override
    // Changing it no longer affects the Exchange Rate or Foreign Total
    const val = Number(newVal || 0);
    formOffer.grand_total_final = val;
};

watch(calculatedPricePerPax, (newVal) => {
    if (formOffer.price !== newVal) {
        formOffer.price = newVal;
    }
});

watch(finalTotal, (newVal) => {
    if (formOffer.grand_total !== newVal) {
        formOffer.grand_total = newVal;
    }
});

const foreignFinalTotalInput = computed({
    get() {
        // Return exactly what the admin input manual, fallback to calculated if empty
        return Math.round(formOffer.grand_total_foreign || 0);
    },
    set(newVal) {
        const targetForeign = Math.round(Number(newVal || 0));
        formOffer.grand_total_foreign = targetForeign; // Save rounded integer

        if (targetForeign > 0) {
            // Recalculate rate based STRICTLY on calculated IDR total (Rp 2.127.273)
            // Sales IDR (Rp 2.200.000) is independent.
            const sourceIdr = Number(finalTotal.value || 0);
            formOffer.exchange_rate = sourceIdr / targetForeign;
        }
    }
});

const selectedCurrency = computed(() => {
    return currencies.value.find(c => c.uuid === formOffer.currency_uuid) || null;
});

const formatCurrency = (value) => {
    if (!selectedCurrency.value) return formatRupiah(value);

    // If currency is IDR, use formatRupiah
    if (selectedCurrency.value.code === 'IDR') {
        return formatRupiah(value);
    }

    // Use Intl.NumberFormat with the currency locale
    const locale = selectedCurrency.value.locale || 'en-US';

    const numberFormatter = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        useGrouping: true // Keeps the thousand separator but digits are 0
    });

    // Manual symbol placement to stay consistent with previous UI
    return `${selectedCurrency.value.symbol} ${numberFormatter.format(Number(value || 0))}`;
};

// const formatForeignNumber = (value) => {
//     if (!selectedCurrency.value) return formatDecimal(value);

//     const locale = selectedCurrency.value.locale || 'en-US';
//     const formatter = new Intl.NumberFormat(locale, {
//         minimumFractionDigits: 0,
//         maximumFractionDigits: 2
//     });

//     return formatter.format(Number(value || 0));
// };

const submitForm = () => {
    submitted.value = true;
    v$.value.$touch();
    if (v$.value.$invalid) {
        // Debug: Show which fields are invalid
        console.log('=== VALIDATION ERRORS ===');
        console.log('Total errors:', v$.value.$errors.length);

        // Show each error in detail
        v$.value.$errors.forEach((error, idx) => {
            console.log(`Error ${idx + 1}:`, {
                property: error.$property,
                propertyPath: error.$propertyPath,
                message: error.$message,
                validator: error.$validator
            });
        });

        formOffer.programs.forEach((prog, idx) => {
            console.log(`Program ${idx + 1}:`, {
                name: prog.name,
                from_destination_uuid: prog.from_destination_uuid,
                to_destination_uuid: prog.to_destination_uuid
            });
        });

        toast.error("Please fill all required fields correctly.");
        return;
    }
    isSavingFlow.value = true;
    showPreview.value = true;
};

const performSave = async () => {
    const loader = $loading.show();
    try {
        const payload = {
            ...formOffer,
            exchange_rate: selectedCurrency.value?.code === 'IDR' ? 1 : formOffer.exchange_rate,
            category: 'Direct',
            trans_date: formOffer.trans_date || null,
            start: formOffer.start || null,
            end: formOffer.end || null,
            programs: formOffer.programs.map(p => ({
                ...p,
                date: p.date || null
            })),
            price: formOffer.price,
            subtotal: formOffer.subtotal,
            discount_type: discountType.value,
            discount_amount: formOffer.discount_amount,
            tax_amount: formOffer.tax_amount,
            service_amount: formOffer.service_amount,
            single_supplement: formOffer.single_supplement,
            grand_total: formOffer.grand_total,
            grand_total_final: formOffer.grand_total_final,
            grand_total_foreign: Math.round(Number(formOffer.grand_total_foreign || 0)),
            deposit_amount_foreign: Math.round(Number(formOffer.deposit_amount_foreign || 0))
        };

        console.log(payload);

        const url = isEdit.value ? `${process.env.VUE_APP_API_URL}/offer/edit/${route.params.uuid}` : `${process.env.VUE_APP_API_URL}/offer/add`;
        const method = isEdit.value ? 'put' : 'post';

        const res = await axios[method](url, payload, {
            headers: { Authorization: "Bearer " + auth._token }
        });

        if (res.data.status) {
            showPreview.value = false;
            toast.success(res.data.message);
            localStorage.removeItem('offer_document_draft');
            router.push('/offerdocuments');
        }
    } catch (e) {
        console.error(e);
        toast.error("Failed to save data");
    } finally {
        loader.hide();
    }
};

const loadOfferData = async (uuid) => {
    const response = await axios.get(`${process.env.VUE_APP_API_URL}/offer/detail/${uuid}`, {
        headers: { Authorization: "Bearer " + auth._token }
    });
    const d = response.data.data;
    console.log("DEBUG LOAD OFFER:", d);
    const robustData = {
        ...d,
        guest_uuid: d.guest_uuid || (d.guest?.uuid || ''),
        currency_uuid: d.currency_uuid || (d.currency?.uuid || ''),
        payment_method_uuid: d.payment_method_uuid || (d.paymentMethod?.uuid || ''),
        entrance_fee: (d.entrance_fee || 0) / (d.pax || 1),
        ferry_fee: (d.ferry_fee || 0) / (d.pax || 1),
        toll_fee: (d.toll_fee || 0) / (d.pax || 1),
        np_permit_fee: (d.np_permit_fee || 0) / (d.pax || 1),
        trans_date: d.trans_date ? d.trans_date.split('T')[0] : '',
        start: d.start ? d.start.split('T')[0] : '',
        end: d.end ? d.end.split('T')[0] : '',
        // Ensure additionals is array
        additionals: Array.isArray(d.additionals) ? d.additionals.map(a => ({
            ...a,
            price: parseFloat(a.price || 0),
            sub_total: parseFloat(a.sub_total || 0),
            currency: a.currency || 'IDR'
        })) : [],
        programs: d.programs.map(p => ({
            ...p,
            price: 0, // Reset base price to 0 to avoid double counting with components
            from_destination_uuid: p.from_destination_uuid || (p.fromDestination?.uuid || null),
            to_destination_uuid: p.to_destination_uuid || (p.toDestination?.uuid || null),
            transportations: p.transportations || [],
            accommodations: p.accommodations || [],
            activities: p.activities || [],
            meals: Array.isArray(p.meals) ? p.meals.map(m => ({
                ...m,
                pax: m.pax || d.pax || 1
            })) : []
        }))
    };

    if (robustData.discount_type) {
        discountType.value = robustData.discount_type;
    } else {
        if (robustData.discount_amount > 0 && (robustData.discount || 0) == 0) discountType.value = 'nominal';
        else discountType.value = 'percent';
    }

    if (robustData.grand_total_final === 0 || robustData.grand_total_final === null) {
        robustData.grand_total_final = robustData.grand_total;
    }

    Object.assign(formOffer, robustData);
    
    // Force exchange_rate to 1 if currency is IDR
    if (robustData.currency?.code === 'IDR') {
        formOffer.exchange_rate = 1;
    }
};


</script>

<style scoped>
.x-small {
    font-size: 0.75rem;
}

.small {
    font-size: 0.85rem;
}

.italic {
    font-style: italic;
}

.border-left-primary {
    border-left: 5px solid #007bff !important;
}

/* PDF Preview Styles */
.pdf-preview-container {
    min-height: 1000px;
    background-color: #fff;
}

.watermark-bg {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 70vw;
    height: 70vh;
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    opacity: 0.03;
    z-index: -1;
    pointer-events: none;
}

.logo-fallback {
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #f8f9fa;
    border: 1px solid #ddd;
}

.pricing-row {
    background-color: #2e4d2e !important;
    border-top: 2px solid #000 !important;
}

.supplement-row {
    background-color: #e2efda !important;
}

.pdf-activity-content ul {
    padding-left: 1.2rem;
}

.grayscale {
    filter: grayscale(100%);
}

.opacity-50 {
    opacity: 0.5;
}
</style>

<!-- Global print styles (non-scoped) -->
<style>
/* Hide printable document in normal view (it has d-none class) */
/* This selector is for specificity if needed */

@media print {
    @page {
        margin: 1cm;
        size: A4;
    }

    /* Hide EVERYTHING on the page */
    body * {
        visibility: hidden !important;
    }

    /* Hide modal preview content during print */
    .modal-preview-content {
        display: none !important;
    }

    /* Show the printable document (override d-none) */
    #printable-document {
        display: block !important;
        visibility: visible !important;
    }

    /* Show only the printable document and its children */
    #printable-document,
    #printable-document * {
        visibility: visible !important;
    }

    /* Position the document at top-left corner of page */
    #printable-document {
        position: relative !important;
        display: block !important;
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
        min-height: 29.7cm !important;
        box-shadow: none !important;
        border: none !important;
        background: transparent !important;
        overflow: visible !important;
        visibility: visible !important;
    }

    /* Reset modal containers */
    .modal,
    .modal-dialog,
    .modal-content,
    .modal-body {
        position: static !important;
        overflow: visible !important;
        height: auto !important;
        max-height: none !important;
        width: 100% !important;
        max-width: none !important;
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
        padding: 0 !important;
        margin: 0 !important;
        transform: none !important;
    }

    /* Hide all UI elements */
    .modal-header,
    .modal-footer,
    .d-print-none,
    .sidebar,
    .navbar,
    .content-wrapper>*:not(.modal),
    .main-sidebar,
    .main-header,
    .card:not(#printable-document .card) {
        display: none !important;
        visibility: hidden !important;
        height: 0 !important;
        overflow: hidden !important;
    }

    /* Ensure colors print correctly and transparency for watermark visibility */
    * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }

    /* Make tables transparent so watermark shows through */
    .bank-details,
    .bg-light {
        background-color: transparent !important;
    }

    /* Preserve background colors on specific rows but with transparency */
    .pricing-row {
        background-color: rgba(46, 77, 46, 0.8) !important;
        color: white !important;
    }

    .supplement-row {
        background-color: rgba(226, 239, 218, 0.5) !important;
    }
}
</style>
